import { useEffect, useMemo, useState } from 'react';
import type {
  AiFeedbackEntry,
  AiFeedbackStats,
  AiTrainingRecommendation,
  AiTrainingSettings,
  InboundVoiceSettings,
  RagSettings,
  RagTelemetryOverview,
  RealtimeTelemetryOverview,
  RoutingMode,
  TelephonyNumberInventoryEntry,
  TelephonyNumberMigrationState,
  TelephonyRoutingReadiness,
  TeamIntegrations,
  VoiceProvider,
  VoiceMediaAsset,
  VoiceMediaAssetPurpose,
  Weekday,
} from '@callhub/shared';
import { Button, Input, TextArea } from '@/components/ui-next';
import { AuroraBackground } from '@/components/ui/aurora-background';
import {
  RoutingReadinessStrip,
  type ReadinessStatusTone,
} from '@/components/RoutingReadinessStrip';
import { useThemeMode } from '@/hooks/useThemeMode';
import { api } from '@/lib/api';
import {
  TYPOGRAPHY_SCALE_PREFERENCE_KEY,
  adjustTypographyScale,
  applyTypographyScale,
  getStoredTypographyScale,
  normalizeTypographyScale,
  setStoredTypographyScale,
  type TypographyScale,
  type TypographyScaleKey,
} from '@/lib/typography';
import {
  getMaxTokensEstimate,
  getTemperatureEstimate,
  normalizeTemperatureInput,
} from './settings-estimates';
import { rankModels, type RankedModelOption } from './settings-models';
import { DEFAULT_RAG_SETTINGS, normalizeRagSettingsForSave } from './settings-rag';
import { buildRagTelemetryAlerts, resolveRagTelemetryHealth } from './settings-rag-telemetry';
import {
  buildRealtimeIngressRecoveryActions,
  buildRealtimeIngressRecoveryReasons,
  buildRealtimeTelemetryAlerts,
  resolveRealtimeTelemetryHealth,
} from './settings-realtime-telemetry';
import {
  DEFAULT_TELEMETRY_LOOKBACK_DAYS,
  TELEMETRY_LOOKBACK_OPTIONS,
  normalizeTelemetryLookbackDays,
} from './settings-telemetry-lookback';
import { CrmBusinessSettings } from './settings-crm-business';

const CRM_V2_ENABLED = (import.meta.env.VITE_CRM_V2_ENABLED ?? '').toLowerCase() === 'true';

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatSignedPercent(value: number): string {
  const rounded = Math.round(value * 100);
  if (rounded > 0) return `+${rounded}%`;
  return `${rounded}%`;
}

function formatSettingsSaveError(error: unknown): string {
  if (!(error instanceof Error) || error.message.trim().length === 0) {
    return 'Failed to save settings.';
  }

  return error.message.replace(/^API error:\s+\d+\s+.*?\s-\s+/i, '').trim();
}

function resolveRealtimeTelemetryTone(
  severity: 'healthy' | 'watch' | 'critical',
): ReadinessStatusTone {
  if (severity === 'healthy') return 'good';
  return severity;
}

function buildRoutingProviderReadinessPreview(input: {
  twilioAccountSid: string;
  twilioAuthToken: string;
  twilioPhoneNumber: string;
  telnyxApiKey: string;
  telnyxPhoneNumber: string;
  signalwireSpaceUrl: string;
  signalwireProjectId: string;
  signalwireApiToken: string;
  signalwirePhoneNumber: string;
  baselineProviders?: TelephonyRoutingReadiness['providers'] | null;
}): TelephonyRoutingReadiness['providers'] {
  const twilioReady = Boolean(
    input.twilioAccountSid.trim() && input.twilioAuthToken.trim() && input.twilioPhoneNumber.trim(),
  );
  const telnyxReady = Boolean(input.telnyxApiKey.trim() && input.telnyxPhoneNumber.trim());
  const signalwireReady = Boolean(
    input.signalwireSpaceUrl.trim() &&
    input.signalwireProjectId.trim() &&
    input.signalwireApiToken.trim() &&
    input.signalwirePhoneNumber.trim(),
  );
  const baseline = input.baselineProviders ?? null;

  return {
    twilio: twilioReady
      ? {
          ready: true,
          reason:
            baseline?.twilio.ready === true
              ? baseline.twilio.reason
              : 'Twilio credentials are configured for account auth and inbound number routing.',
          recommendation:
            baseline?.twilio.ready === true
              ? baseline.twilio.recommendation
              : 'No changes required.',
        }
      : {
          ready: false,
          reason:
            baseline?.twilio.ready === false
              ? baseline.twilio.reason
              : 'Twilio needs Account SID, Auth Token, and Twilio phone number.',
          recommendation:
            baseline?.twilio.ready === false
              ? baseline.twilio.recommendation
              : 'Set Twilio Account SID, Auth Token, and Twilio Phone Number before using Twilio in provider router mode.',
        },
    telnyx: telnyxReady
      ? {
          ready: true,
          reason:
            baseline?.telnyx.ready === true
              ? baseline.telnyx.reason
              : 'Telnyx API credentials are configured for outbound messaging and webhook routing.',
          recommendation:
            baseline?.telnyx.ready === true
              ? baseline.telnyx.recommendation
              : 'No changes required.',
        }
      : {
          ready: false,
          reason:
            baseline?.telnyx.ready === false
              ? baseline.telnyx.reason
              : 'Telnyx requires API key and a Telnyx sender number for outbound SMS.',
          recommendation:
            baseline?.telnyx.ready === false
              ? baseline.telnyx.recommendation
              : 'Set Telnyx API key and Telnyx sender number before using Telnyx as an active provider.',
        },
    signalwire: signalwireReady
      ? {
          ready: true,
          reason:
            baseline?.signalwire.ready === true
              ? baseline.signalwire.reason
              : 'SignalWire project credentials are configured for outbound messaging and webhook routing.',
          recommendation:
            baseline?.signalwire.ready === true
              ? baseline.signalwire.recommendation
              : 'No changes required.',
        }
      : {
          ready: false,
          reason:
            baseline?.signalwire.ready === false
              ? baseline.signalwire.reason
              : 'SignalWire requires space URL, project ID, API token, and a SignalWire sender number.',
          recommendation:
            baseline?.signalwire.ready === false
              ? baseline.signalwire.recommendation
              : 'Set SignalWire space URL, project ID, API token, and sender number before using SignalWire as an active provider.',
        },
  };
}

function buildRoutingReadinessPreview(input: {
  routingMode: RoutingMode;
  voicePrimaryProvider: VoiceProvider;
  voiceFailoverProviders: VoiceProvider[];
  smsPrimaryProvider: VoiceProvider;
  smsFailoverProviders: VoiceProvider[];
  defaultNumberOwnerProvider: VoiceProvider;
  twilioAccountSid: string;
  twilioAuthToken: string;
  twilioApiKey: string;
  twilioApiSecret: string;
  twilioPhoneNumber: string;
  telnyxApiKey: string;
  telnyxPhoneNumber: string;
  signalwireSpaceUrl: string;
  signalwireProjectId: string;
  signalwireApiToken: string;
  signalwirePhoneNumber: string;
  baselineProviders?: TelephonyRoutingReadiness['providers'] | null;
}): TelephonyRoutingReadiness {
  const providers = buildRoutingProviderReadinessPreview({
    twilioAccountSid: input.twilioAccountSid,
    twilioAuthToken: input.twilioAuthToken,
    twilioPhoneNumber: input.twilioPhoneNumber,
    telnyxApiKey: input.telnyxApiKey,
    telnyxPhoneNumber: input.telnyxPhoneNumber,
    signalwireSpaceUrl: input.signalwireSpaceUrl,
    signalwireProjectId: input.signalwireProjectId,
    signalwireApiToken: input.signalwireApiToken,
    signalwirePhoneNumber: input.signalwirePhoneNumber,
    baselineProviders: input.baselineProviders,
  });
  const blockingIssues: string[] = [];
  let hasProviderRouterBlocker = false;

  const validateFailoverPolicy = (
    primaryProvider: VoiceProvider,
    failoverProviders: VoiceProvider[],
    fieldLabel: 'voiceFailoverProviders' | 'smsFailoverProviders',
    primaryLabel: 'voice' | 'SMS',
  ) => {
    if (failoverProviders.length === 0) {
      blockingIssues.push(
        `${fieldLabel}: ${primaryLabel} failover providers must include at least one failover provider. Recommendation: Select at least one secondary provider so ${primaryLabel.toLowerCase()} can retry pre-answer or pre-send failures.`,
      );
      return;
    }

    if (failoverProviders.includes(primaryProvider)) {
      blockingIssues.push(
        `${fieldLabel}: ${primaryLabel} failover providers cannot include the ${primaryLabel.toLowerCase()} primary provider ("${primaryProvider}"). Recommendation: Remove "${primaryProvider}" from ${fieldLabel} and keep it only as the primary provider.`,
      );
    }

    const duplicates = [
      ...new Set(
        failoverProviders.filter(
          (provider, index) => failoverProviders.indexOf(provider) !== index,
        ),
      ),
    ];
    if (duplicates.length > 0) {
      blockingIssues.push(
        `${fieldLabel}: ${primaryLabel} failover providers contains duplicate providers (${duplicates.join(', ')}). Recommendation: Keep each failover provider once so retry order is deterministic.`,
      );
    }
  };

  validateFailoverPolicy(
    input.voicePrimaryProvider,
    input.voiceFailoverProviders,
    'voiceFailoverProviders',
    'voice',
  );
  validateFailoverPolicy(
    input.smsPrimaryProvider,
    input.smsFailoverProviders,
    'smsFailoverProviders',
    'SMS',
  );

  const hasApiKey = input.twilioApiKey.trim().length > 0;
  const hasApiSecret = input.twilioApiSecret.trim().length > 0;
  if (hasApiKey !== hasApiSecret) {
    blockingIssues.push(
      'twilioApiSecret: Twilio API key and API secret must be provided together. Recommendation: Set both fields together or clear both fields to avoid partial credentials.',
    );
  }

  if (input.routingMode === 'provider_router') {
    const requireProviderReadiness = (
      provider: VoiceProvider,
      fieldLabel: string,
      index?: number,
    ) => {
      const providerReadiness = providers[provider];
      if (providerReadiness.ready) return;
      hasProviderRouterBlocker = true;
      const label = index === undefined ? fieldLabel : `${fieldLabel}[${index}]`;
      blockingIssues.push(
        `${label} uses "${provider}", but readiness is not complete. ${providerReadiness.reason} Recommendation: ${providerReadiness.recommendation}`,
      );
    };

    requireProviderReadiness(input.voicePrimaryProvider, 'voicePrimaryProvider');
    requireProviderReadiness(input.smsPrimaryProvider, 'smsPrimaryProvider');
    requireProviderReadiness(input.defaultNumberOwnerProvider, 'defaultNumberOwnerProvider');
  }

  return {
    recommendedRoutingMode:
      input.routingMode === 'provider_router' && hasProviderRouterBlocker
        ? 'twilio_legacy'
        : input.routingMode,
    blockingIssues,
    providers,
  };
}

function normalizeRoutingProvider(value: unknown, fallback: VoiceProvider): VoiceProvider {
  if (value === 'twilio' || value === 'telnyx' || value === 'signalwire') {
    return value;
  }
  return fallback;
}

function normalizeFailoverProviders(value: unknown, primary: VoiceProvider): VoiceProvider[] {
  const raw = Array.isArray(value) ? value : [];
  const selected: VoiceProvider[] = [];

  for (const item of raw) {
    const normalized = normalizeRoutingProvider(item, primary);
    if (normalized === primary || selected.includes(normalized)) continue;
    selected.push(normalized);
  }

  if (selected.length > 0) return selected;
  return ROUTING_PROVIDER_OPTIONS.filter((provider) => provider !== primary);
}

function normalizeNumberMigrationState(
  value: unknown,
  fallback: TelephonyNumberMigrationState = 'staged',
): TelephonyNumberMigrationState {
  if (
    typeof value === 'string' &&
    TELEPHONY_NUMBER_MIGRATION_OPTIONS.includes(value as TelephonyNumberMigrationState)
  ) {
    return value as TelephonyNumberMigrationState;
  }
  return fallback;
}

function normalizeNumberInventory(
  value: unknown,
  fallbackOwnerProvider: VoiceProvider,
): TelephonyNumberInventoryEntry[] {
  const rows = Array.isArray(value) ? value : [];
  const entries: TelephonyNumberInventoryEntry[] = [];
  const seenPhoneNumbers = new Set<string>();

  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const next = row as Partial<TelephonyNumberInventoryEntry>;
    const phoneNumber = typeof next.phoneNumber === 'string' ? next.phoneNumber.trim() : '';
    if (!phoneNumber || seenPhoneNumbers.has(phoneNumber)) continue;

    seenPhoneNumbers.add(phoneNumber);
    const ownerProvider = normalizeRoutingProvider(next.ownerProvider, fallbackOwnerProvider);
    entries.push({
      phoneNumber,
      ownerProvider,
      smsProvider: normalizeRoutingProvider(next.smsProvider, ownerProvider),
      voiceProvider: normalizeRoutingProvider(next.voiceProvider, ownerProvider),
      migrationState: normalizeNumberMigrationState(next.migrationState, 'staged'),
    });
  }

  return entries;
}

function sanitizeFailoverProviders(value: unknown, primary: VoiceProvider): VoiceProvider[] {
  const raw = Array.isArray(value) ? value : [];
  const selected: VoiceProvider[] = [];

  for (const item of raw) {
    const normalized = normalizeRoutingProvider(item, primary);
    if (normalized === primary || selected.includes(normalized)) continue;
    selected.push(normalized);
  }

  return selected;
}

function normalizeTeamIntegrations(
  raw: Partial<TeamIntegrations> | null | undefined,
): TeamIntegrations {
  const next = raw ?? {};
  const routingMode: RoutingMode =
    next.routingMode === 'provider_router' ? 'provider_router' : 'twilio_legacy';
  const voicePrimaryProvider = normalizeRoutingProvider(
    next.voicePrimaryProvider,
    DEFAULT_TEAM_INTEGRATIONS.voicePrimaryProvider,
  );
  const smsPrimaryProvider = normalizeRoutingProvider(
    next.smsPrimaryProvider,
    DEFAULT_TEAM_INTEGRATIONS.smsPrimaryProvider,
  );

  return {
    twilioAccountSid: next.twilioAccountSid ?? '',
    twilioAuthToken: next.twilioAuthToken ?? '',
    twilioApiKey: next.twilioApiKey ?? '',
    twilioApiSecret: next.twilioApiSecret ?? '',
    twilioPhoneNumber: next.twilioPhoneNumber ?? '',
    telnyxApiKey: next.telnyxApiKey ?? '',
    telnyxMessagingProfileId: next.telnyxMessagingProfileId ?? '',
    telnyxPhoneNumber: next.telnyxPhoneNumber ?? '',
    signalwireSpaceUrl: next.signalwireSpaceUrl ?? '',
    signalwireProjectId: next.signalwireProjectId ?? '',
    signalwireApiToken: next.signalwireApiToken ?? '',
    signalwirePhoneNumber: next.signalwirePhoneNumber ?? '',
    routingMode,
    voicePrimaryProvider,
    voiceFailoverProviders: normalizeFailoverProviders(
      next.voiceFailoverProviders,
      voicePrimaryProvider,
    ),
    smsPrimaryProvider,
    smsFailoverProviders: normalizeFailoverProviders(next.smsFailoverProviders, smsPrimaryProvider),
    defaultNumberOwnerProvider: normalizeRoutingProvider(
      next.defaultNumberOwnerProvider,
      DEFAULT_TEAM_INTEGRATIONS.defaultNumberOwnerProvider,
    ),
    routingReadiness: next.routingReadiness,
    numberInventory: normalizeNumberInventory(
      next.numberInventory,
      normalizeRoutingProvider(
        next.defaultNumberOwnerProvider,
        DEFAULT_TEAM_INTEGRATIONS.defaultNumberOwnerProvider,
      ),
    ),
  };
}

const DEFAULT_PROMPTS = {
  summary:
    'Write a concise customer-support summary in plain English with concrete outcomes and unresolved concerns.',
  highlights:
    'Extract short, high-signal conversation highlights focused on customer intent, blockers, and decisions.',
  actionItems:
    'Extract actionable follow-up tasks with owner hints if available; prefer imperative phrasing.',
} as const;

const ROUTING_PROVIDER_OPTIONS: VoiceProvider[] = ['telnyx', 'twilio', 'signalwire'];
const TELEPHONY_NUMBER_MIGRATION_OPTIONS: TelephonyNumberMigrationState[] = [
  'legacy',
  'staged',
  'pilot',
  'migrated',
];
const DEFAULT_TEAM_INTEGRATIONS: TeamIntegrations = {
  twilioAccountSid: '',
  twilioAuthToken: '',
  twilioApiKey: '',
  twilioApiSecret: '',
  twilioPhoneNumber: '',
  telnyxApiKey: '',
  telnyxMessagingProfileId: '',
  telnyxPhoneNumber: '',
  signalwireSpaceUrl: '',
  signalwireProjectId: '',
  signalwireApiToken: '',
  signalwirePhoneNumber: '',
  routingMode: 'twilio_legacy',
  voicePrimaryProvider: 'telnyx',
  voiceFailoverProviders: ['twilio', 'signalwire'],
  smsPrimaryProvider: 'twilio',
  smsFailoverProviders: ['telnyx', 'signalwire'],
  defaultNumberOwnerProvider: 'twilio',
  numberInventory: [],
};

type PromptKey = keyof typeof DEFAULT_PROMPTS;
type SettingsTopLevelTab = 'ai' | 'business' | 'integrations' | 'appearance';
type AiSettingsView = 'config' | 'training' | 'telemetry';

const SETTINGS_TOP_LEVEL_TABS: Array<{
  key: SettingsTopLevelTab;
  label: string;
  description: string;
}> = [
  {
    key: 'ai',
    label: 'AI',
    description: 'Model controls, prompts, fine-tuning, and telemetry.',
  },
  {
    key: 'business',
    label: 'Business',
    description: 'Inbound call policy, greetings, schedule, and voicemail behavior.',
  },
  {
    key: 'integrations',
    label: 'Integrations',
    description: 'Provider credentials, environment sync, and service readiness.',
  },
  {
    key: 'appearance',
    label: 'Appearance',
    description: 'Theme, typography, and visual workspace preferences.',
  },
];

const AI_SETTINGS_VIEWS: Array<{
  key: AiSettingsView;
  label: string;
  description: string;
}> = [
  {
    key: 'config',
    label: 'Config',
    description: 'Edit model routing, prompts, and RAG behavior.',
  },
  {
    key: 'training',
    label: 'Fine-tuning',
    description: 'Review feedback signals and apply training recommendations.',
  },
  {
    key: 'telemetry',
    label: 'Telemetry',
    description: 'Monitor RAG and realtime transcript quality trends.',
  },
];

const PROMPT_EDITOR_TABS: Array<{
  key: PromptKey;
  label: string;
  description: string;
  tip: string;
}> = [
  {
    key: 'summary',
    label: 'Summary Prompt',
    description: 'Drives the AI Summary card with a concise final narrative.',
    tip: 'Tip: include outcome + blockers + next step guidance.',
  },
  {
    key: 'highlights',
    label: 'Highlights Prompt',
    description: 'Controls which conversation moments appear as bullet highlights.',
    tip: 'Tip: keep this focused on signal, not full restatement.',
  },
  {
    key: 'actionItems',
    label: 'Action Items Prompt',
    description: 'Extracts operational follow-ups from the conversation.',
    tip: 'Tip: ask for imperative tasks with ownership when available.',
  },
];

const EMPTY_FEEDBACK_BUCKET = {
  total: 0,
  positive: 0,
  negative: 0,
  positiveRate: 0,
  negativeRate: 0,
} as const;

const EMPTY_FEEDBACK_STATS: AiFeedbackStats = {
  ...EMPTY_FEEDBACK_BUCKET,
  byTarget: {
    summary: { ...EMPTY_FEEDBACK_BUCKET },
    highlight: { ...EMPTY_FEEDBACK_BUCKET },
    action_item: { ...EMPTY_FEEDBACK_BUCKET },
    voice_transcript: { ...EMPTY_FEEDBACK_BUCKET },
  },
};

const EMPTY_TRAINING_RECOMMENDATION: AiTrainingRecommendation = {
  hasChanges: false,
  nextModelParams: { temperature: 0.55, maxTokens: 500 },
  reasons: [],
};

const EMPTY_RAG_TELEMETRY: RagTelemetryOverview = {
  lookbackDays: 30,
  generatedAt: new Date(0).toISOString(),
  feedback: {
    total: 0,
    accepted: 0,
    rejected: 0,
    acceptanceRate: 0,
    rejectionRate: 0,
  },
  suggestionStatus: {
    proposed: 0,
    applied: 0,
    accepted: 0,
    rejected: 0,
  },
  byConversation: [],
  recentFeedback: [],
};

const EMPTY_REALTIME_TELEMETRY: RealtimeTelemetryOverview = {
  lookbackDays: 30,
  generatedAt: new Date(0).toISOString(),
  totalTranscriptChunks: 0,
  uniqueConversations: 0,
  uniqueCalls: 0,
  fallbackChunks: 0,
  fallbackRate: 0,
  streamsWithStopChunk: 0,
  streamsWithoutStopChunk: 0,
  stopChunkRate: 0,
  unfinalizedStreams: 0,
  unfinalizedStreamRate: 0,
  staleUnfinalizedStreams: 0,
  streamsWithAttemptFailures: 0,
  streamsWithEmptyTranscripts: 0,
  streamsWithDelayedGaps: 0,
  streamAttemptFailureRate: 0,
  streamDelayedGapRate: 0,
  streamEmptyTranscriptRate: 0,
  emptyTranscriptChunks: 0,
  emptyTranscriptRate: 0,
  attemptFailureChunks: 0,
  attemptFailureRate: 0,
  averageChunkGapSeconds: 0,
  averageTranscriptChars: 0,
  averageAttemptsPerChunk: 0,
  largestObservedGapSeconds: 0,
  chunkSources: {
    chunk: 0,
    stop: 0,
    other: 0,
  },
  trend: {
    previousWindowChunks: 0,
    fallbackRateDelta: 0,
    attemptFailureRateDelta: 0,
    emptyTranscriptRateDelta: 0,
    stopChunkRateDelta: 0,
    streamAttemptFailureRateDelta: 0,
    streamDelayedGapRateDelta: 0,
    streamEmptyTranscriptRateDelta: 0,
  },
  topAttemptErrors: [],
  ingressDiagnostics: {
    total: 0,
    counts: {
      invalid_stream_token: 0,
      unauthorized_before_start: 0,
      missing_stream_sid: 0,
      mismatched_stream_sid: 0,
      missing_call_record: 0,
      call_record_conversation_mismatch: 0,
      missing_team_scoped_conversation: 0,
    },
    latestByReason: {
      invalid_stream_token: null,
      unauthorized_before_start: null,
      missing_stream_sid: null,
      mismatched_stream_sid: null,
      missing_call_record: null,
      call_record_conversation_mismatch: null,
      missing_team_scoped_conversation: null,
    },
    recentIncidents: [],
  },
  topUnfinalizedStreams: [],
  topDelayedStreams: [],
  models: [],
  recentStreams: [],
};

const TYPOGRAPHY_CONTROL_ORDER: Array<{
  key: TypographyScaleKey;
  label: string;
  description: string;
}> = [
  {
    key: 'titles',
    label: 'Titles',
    description: 'Page-level headings and major section titles (text-2xl / text-xl).',
  },
  {
    key: 'subtitles',
    label: 'Subtitles',
    description: 'Card and panel section headers (text-lg).',
  },
  {
    key: 'body',
    label: 'Body',
    description: 'Primary paragraph and long-form content text (text-base).',
  },
  {
    key: 'labels',
    label: 'Labels',
    description: 'Navigation items, form labels, and button copy (text-sm).',
  },
  {
    key: 'captions',
    label: 'Captions',
    description: 'Hints, helper copy, badges, and metadata text (text-xs).',
  },
];

const INBOUND_WEEKDAYS: Weekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const COMMON_TIMEZONES = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'Europe/London',
] as const;
const VOICE_MEDIA_UPLOAD_ALLOWED_MIME_TYPES = new Set([
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/wave',
  'audio/x-wav',
  'audio/vnd.wave',
]);

type ProviderWebhookReadinessRow = {
  provider: 'Twilio' | 'Telnyx' | 'SignalWire';
  channel: 'SMS' | 'Voice' | 'Call Status' | 'Recording';
  path: string;
  readiness: 'Live' | 'Reserved';
  note: string;
};

const PROVIDER_WEBHOOK_READINESS_ROWS: ProviderWebhookReadinessRow[] = [
  {
    provider: 'Twilio',
    channel: 'SMS',
    path: '/api/webhooks/twilio/sms',
    readiness: 'Live',
    note: 'Active production webhook path.',
  },
  {
    provider: 'Twilio',
    channel: 'Voice',
    path: '/api/webhooks/twilio/voice',
    readiness: 'Live',
    note: 'Active production webhook path.',
  },
  {
    provider: 'Twilio',
    channel: 'Call Status',
    path: '/api/webhooks/twilio/call-status',
    readiness: 'Live',
    note: 'Active production webhook path.',
  },
  {
    provider: 'Twilio',
    channel: 'Recording',
    path: '/api/webhooks/twilio/recording',
    readiness: 'Live',
    note: 'Active production webhook path.',
  },
  {
    provider: 'Telnyx',
    channel: 'SMS',
    path: '/api/webhooks/telnyx/sms',
    readiness: 'Reserved',
    note: 'Endpoint is ready; signature verification and normalized processing remain planned.',
  },
  {
    provider: 'Telnyx',
    channel: 'Voice',
    path: '/api/webhooks/telnyx/voice',
    readiness: 'Reserved',
    note: 'Endpoint is ready; signature verification and normalized processing remain planned.',
  },
  {
    provider: 'Telnyx',
    channel: 'Call Status',
    path: '/api/webhooks/telnyx/call-status',
    readiness: 'Reserved',
    note: 'Endpoint is ready; signature verification and normalized processing remain planned.',
  },
  {
    provider: 'Telnyx',
    channel: 'Recording',
    path: '/api/webhooks/telnyx/recording',
    readiness: 'Reserved',
    note: 'Endpoint is ready; signature verification and normalized processing remain planned.',
  },
  {
    provider: 'SignalWire',
    channel: 'SMS',
    path: '/api/webhooks/signalwire/sms',
    readiness: 'Reserved',
    note: 'Endpoint is ready; adapter enablement remains planned.',
  },
  {
    provider: 'SignalWire',
    channel: 'Voice',
    path: '/api/webhooks/signalwire/voice',
    readiness: 'Reserved',
    note: 'Endpoint is ready; adapter enablement remains planned.',
  },
  {
    provider: 'SignalWire',
    channel: 'Call Status',
    path: '/api/webhooks/signalwire/call-status',
    readiness: 'Reserved',
    note: 'Endpoint is ready; adapter enablement remains planned.',
  },
  {
    provider: 'SignalWire',
    channel: 'Recording',
    path: '/api/webhooks/signalwire/recording',
    readiness: 'Reserved',
    note: 'Endpoint is ready; adapter enablement remains planned.',
  },
];

const DEFAULT_INBOUND_VOICE_SETTINGS: InboundVoiceSettings = {
  timezone: 'America/Los_Angeles',
  ringTimeoutSeconds: 20,
  welcomeGreetingEnabled: false,
  voicemailEnabled: true,
  unavailableBehavior: 'voicemail',
  welcomeGreetingAssetId: null,
  afterHoursGreetingAssetId: null,
  voicemailGreetingAssetId: null,
  recordingNoticeAssetId: null,
  schedule: INBOUND_WEEKDAYS.map((day) => ({
    day,
    enabled:
      day === 'monday' ||
      day === 'tuesday' ||
      day === 'wednesday' ||
      day === 'thursday' ||
      day === 'friday',
    startTime:
      day === 'monday' ||
      day === 'tuesday' ||
      day === 'wednesday' ||
      day === 'thursday' ||
      day === 'friday'
        ? '09:00'
        : null,
    endTime:
      day === 'monday' ||
      day === 'tuesday' ||
      day === 'wednesday' ||
      day === 'thursday' ||
      day === 'friday'
        ? '17:00'
        : null,
  })),
};

type InboundVoiceScheduleWindow = InboundVoiceSettings['schedule'][number];

function formatWeekdayLabel(day: Weekday) {
  return `${day.slice(0, 1).toUpperCase()}${day.slice(1)}`;
}

type ModelSelectProps = {
  label: string;
  helper: string;
  value: string;
  options: RankedModelOption[];
  onChange: (value: string) => void;
};

function ModelSelect({ label, helper, value, options, onChange }: ModelSelectProps) {
  const selected = options.find((option) => option.id === value) ?? null;

  return (
    <div className="space-y-2">
      <label className="text-sm text-foreground">
        <span className="block text-xs text-muted-foreground">{label}</span>
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="h-10 w-full appearance-none rounded-xl border border-border bg-background px-3 pr-10 text-sm text-foreground transition-colors focus:border-brand-500 focus:outline-none"
        >
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label} • Q{option.quality}/5 • Cost {option.cost}/5
            </option>
          ))}
        </select>
        <span className="pointer-events-none material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-[18px] text-muted-foreground">
          expand_more
        </span>
      </div>
      {selected ? (
        <p className="text-xs text-muted-foreground">
          Quality: <span className="font-medium text-foreground">{selected.qualityLabel}</span> •
          Cost: <span className="font-medium text-foreground">{selected.costLabel}</span> • Speed:{' '}
          <span className="font-medium text-foreground">{selected.speed}/5</span>
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground">{helper}</p>
    </div>
  );
}

function formatFeedbackTarget(target: AiFeedbackEntry['targetType']) {
  if (target === 'summary') return 'AI Summary';
  if (target === 'highlight') return 'Highlights';
  if (target === 'action_item') return 'Action Items';
  return 'Voice Transcript';
}

export function SettingsPage() {
  const { themeMode, resolvedTheme, setThemeMode } = useThemeMode();
  const [activeTopLevelTab, setActiveTopLevelTab] = useState<SettingsTopLevelTab>('ai');
  const [aiSettingsView, setAiSettingsView] = useState<AiSettingsView>('config');
  const [temperatureSlider, setTemperatureSlider] = useState(55);
  const [maxTokens, setMaxTokens] = useState(500);

  const [summaryModel, setSummaryModel] = useState('@cf/meta/llama-3.1-8b-instruct');
  const [highlightModel, setHighlightModel] = useState('@cf/meta/llama-3.1-8b-instruct');
  const [actionItemsModel, setActionItemsModel] = useState('@cf/meta/llama-3.1-8b-instruct');
  const [transcriptModel, setTranscriptModel] = useState('@cf/openai/whisper');

  const [autoSummary, setAutoSummary] = useState(true);
  const [autoHighlights, setAutoHighlights] = useState(true);
  const [autoActionItems, setAutoActionItems] = useState(true);

  const [summaryPrompt, setSummaryPrompt] = useState<string>(DEFAULT_PROMPTS.summary);
  const [highlightsPrompt, setHighlightsPrompt] = useState<string>(DEFAULT_PROMPTS.highlights);
  const [actionItemsPrompt, setActionItemsPrompt] = useState<string>(DEFAULT_PROMPTS.actionItems);
  const [activePromptKey, setActivePromptKey] = useState<PromptKey>('summary');
  const [typographyScale, setTypographyScale] = useState<TypographyScale>(() =>
    getStoredTypographyScale(),
  );
  const [ragEnabled, setRagEnabled] = useState(DEFAULT_RAG_SETTINGS.enabled);
  const [ragManualOnly, setRagManualOnly] = useState(DEFAULT_RAG_SETTINGS.manualOnly);
  const [ragTopK, setRagTopK] = useState(DEFAULT_RAG_SETTINGS.topK);
  const [ragMaxSuggestions, setRagMaxSuggestions] = useState(DEFAULT_RAG_SETTINGS.maxSuggestions);
  const [ragMinConfidence, setRagMinConfidence] = useState(DEFAULT_RAG_SETTINGS.minConfidence);
  const [ragMaxContextChars, setRagMaxContextChars] = useState(
    DEFAULT_RAG_SETTINGS.maxContextChars,
  );
  const [ragEmbeddingModel, setRagEmbeddingModel] = useState(DEFAULT_RAG_SETTINGS.embeddingModel);
  const [ragGenerationModel, setRagGenerationModel] = useState(
    DEFAULT_RAG_SETTINGS.generationModel,
  );
  const [ragPromptTemplate, setRagPromptTemplate] = useState(DEFAULT_RAG_SETTINGS.promptTemplate);
  const [ragTelemetry, setRagTelemetry] = useState<RagTelemetryOverview>(EMPTY_RAG_TELEMETRY);
  const [ragTelemetryLookbackDays, setRagTelemetryLookbackDays] = useState(
    DEFAULT_TELEMETRY_LOOKBACK_DAYS,
  );
  const [loadingRagTelemetry, setLoadingRagTelemetry] = useState(false);
  const [realtimeTelemetry, setRealtimeTelemetry] =
    useState<RealtimeTelemetryOverview>(EMPTY_REALTIME_TELEMETRY);
  const [realtimeTelemetryLookbackDays, setRealtimeTelemetryLookbackDays] = useState(
    DEFAULT_TELEMETRY_LOOKBACK_DAYS,
  );
  const [loadingRealtimeTelemetry, setLoadingRealtimeTelemetry] = useState(false);

  const [textModels, setTextModels] = useState<string[]>([]);
  const [transcriptModels, setTranscriptModels] = useState<string[]>([]);

  const [twilioSid, setTwilioSid] = useState('');
  const [twilioToken, setTwilioToken] = useState('');
  const [twilioApiKey, setTwilioApiKey] = useState('');
  const [twilioApiSecret, setTwilioApiSecret] = useState('');
  const [twilioPhoneNumber, setTwilioPhoneNumber] = useState('');
  const [telnyxApiKey, setTelnyxApiKey] = useState('');
  const [telnyxMessagingProfileId, setTelnyxMessagingProfileId] = useState('');
  const [telnyxPhoneNumber, setTelnyxPhoneNumber] = useState('');
  const [signalwireSpaceUrl, setSignalwireSpaceUrl] = useState('');
  const [signalwireProjectId, setSignalwireProjectId] = useState('');
  const [signalwireApiToken, setSignalwireApiToken] = useState('');
  const [signalwirePhoneNumber, setSignalwirePhoneNumber] = useState('');
  const [routingMode, setRoutingMode] = useState<RoutingMode>('twilio_legacy');
  const [savedRoutingMode, setSavedRoutingMode] = useState<RoutingMode>('twilio_legacy');
  const [voicePrimaryProvider, setVoicePrimaryProvider] = useState<VoiceProvider>('telnyx');
  const [voiceFailoverProviders, setVoiceFailoverProviders] = useState<VoiceProvider[]>([
    'twilio',
    'signalwire',
  ]);
  const [smsPrimaryProvider, setSmsPrimaryProvider] = useState<VoiceProvider>('twilio');
  const [smsFailoverProviders, setSmsFailoverProviders] = useState<VoiceProvider[]>([
    'telnyx',
    'signalwire',
  ]);
  const [defaultNumberOwnerProvider, setDefaultNumberOwnerProvider] =
    useState<VoiceProvider>('twilio');
  const [numberInventory, setNumberInventory] = useState<TelephonyNumberInventoryEntry[]>([]);
  const [inboundVoiceSettings, setInboundVoiceSettings] = useState<InboundVoiceSettings>(
    DEFAULT_INBOUND_VOICE_SETTINGS,
  );
  const [voiceMediaAssets, setVoiceMediaAssets] = useState<VoiceMediaAsset[]>([]);
  const [voiceMediaUploadLabel, setVoiceMediaUploadLabel] = useState('');
  const [voiceMediaUploadPurpose, setVoiceMediaUploadPurpose] =
    useState<VoiceMediaAssetPurpose>('generic');
  const [voiceMediaUploadFile, setVoiceMediaUploadFile] = useState<File | null>(null);
  const [voiceMediaUploadError, setVoiceMediaUploadError] = useState<string | null>(null);
  const [voiceMediaUploadSuccess, setVoiceMediaUploadSuccess] = useState<string | null>(null);
  const [uploadingVoiceMedia, setUploadingVoiceMedia] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingTraining, setSavingTraining] = useState(false);
  const [applyingTraining, setApplyingTraining] = useState(false);
  const [pushingSecrets, setPushingSecrets] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const [routingWarnings, setRoutingWarnings] = useState<string[]>([]);
  const [providerRouterCutoverAcknowledged, setProviderRouterCutoverAcknowledged] = useState(false);
  const [routingReadiness, setRoutingReadiness] = useState<TelephonyRoutingReadiness | null>(null);
  const [trainingEnabled, setTrainingEnabled] = useState(true);
  const [trainingLookbackDays, setTrainingLookbackDays] = useState(30);
  const [trainingMinSamples, setTrainingMinSamples] = useState(8);
  const [trainingNegativeThreshold, setTrainingNegativeThreshold] = useState(0.35);
  const [trainingPositiveThreshold, setTrainingPositiveThreshold] = useState(0.8);
  const [trainingTemperatureStep, setTrainingTemperatureStep] = useState(0.05);
  const [trainingMaxTokensStep, setTrainingMaxTokensStep] = useState(80);
  const [trainingAutoApplyOnCallEnd, setTrainingAutoApplyOnCallEnd] = useState(false);
  const [trainingStats, setTrainingStats] = useState<AiFeedbackStats>(EMPTY_FEEDBACK_STATS);
  const [trainingRecommendation, setTrainingRecommendation] = useState<AiTrainingRecommendation>(
    EMPTY_TRAINING_RECOMMENDATION,
  );
  const [recentFeedback, setRecentFeedback] = useState<AiFeedbackEntry[]>([]);

  useEffect(() => {
    async function load() {
      const loadTeamIntegrations = async (): Promise<Partial<TeamIntegrations> | null> => {
        try {
          const { data } = await api.getTeamIntegrations();
          return data;
        } catch (error) {
          if (error instanceof Error && /^API error:\s*403\b/i.test(error.message)) {
            console.warn(
              'Team integrations are unavailable for this role; continuing with defaults.',
            );
            return null;
          }
          throw error;
        }
      };

      try {
        const [
          { data: aiData },
          integrations,
          { data: training },
          { data: preferences },
          { data: ragSettings },
          { data: ragTelemetryData },
          { data: realtimeTelemetryData },
          { data: inboundVoiceData },
          { data: voiceMediaData },
        ] = await Promise.all([
          api.getAiSettings(),
          loadTeamIntegrations(),
          api.getAiTrainingOverview(),
          api.getMyPreferences(),
          api.getRagSettings(),
          api.getRagTelemetry(),
          api.getRealtimeTelemetry(),
          api.getInboundVoiceSettings(),
          api.getVoiceMediaAssets(),
        ]);

        const aiSettings = aiData.settings;
        setTemperatureSlider(Math.round(aiSettings.modelParams.temperature * 100));
        setMaxTokens(aiSettings.modelParams.maxTokens);
        setSummaryModel(aiSettings.models.summaryModel);
        setHighlightModel(aiSettings.models.highlightModel);
        setActionItemsModel(aiSettings.models.actionItemsModel);
        setTranscriptModel(aiSettings.models.transcriptModel);
        setSummaryPrompt(aiSettings.prompts.summary);
        setHighlightsPrompt(aiSettings.prompts.highlights);
        setActionItemsPrompt(aiSettings.prompts.actionItems);
        setAutoSummary(aiSettings.features.autoSummary);
        setAutoHighlights(aiSettings.features.autoHighlights);
        setAutoActionItems(aiSettings.features.autoActionItems);
        setTextModels(aiData.models.text ?? []);
        setTranscriptModels(aiData.models.transcript ?? []);

        setRagEnabled(ragSettings.enabled);
        setRagManualOnly(ragSettings.manualOnly);
        setRagTopK(ragSettings.topK);
        setRagMaxSuggestions(ragSettings.maxSuggestions);
        setRagMinConfidence(ragSettings.minConfidence);
        setRagMaxContextChars(ragSettings.maxContextChars);
        setRagEmbeddingModel(ragSettings.embeddingModel);
        setRagGenerationModel(ragSettings.generationModel);
        setRagPromptTemplate(ragSettings.promptTemplate);
        setRagTelemetry(ragTelemetryData);
        setRagTelemetryLookbackDays(normalizeTelemetryLookbackDays(ragTelemetryData.lookbackDays));
        setRealtimeTelemetry(realtimeTelemetryData);
        setRealtimeTelemetryLookbackDays(
          normalizeTelemetryLookbackDays(realtimeTelemetryData.lookbackDays),
        );

        const normalizedIntegrations = normalizeTeamIntegrations(integrations);
        setTwilioSid(normalizedIntegrations.twilioAccountSid);
        setTwilioToken(normalizedIntegrations.twilioAuthToken);
        setTwilioApiKey(normalizedIntegrations.twilioApiKey);
        setTwilioApiSecret(normalizedIntegrations.twilioApiSecret);
        setTwilioPhoneNumber(normalizedIntegrations.twilioPhoneNumber);
        setTelnyxApiKey(normalizedIntegrations.telnyxApiKey ?? '');
        setTelnyxMessagingProfileId(normalizedIntegrations.telnyxMessagingProfileId ?? '');
        setTelnyxPhoneNumber(normalizedIntegrations.telnyxPhoneNumber ?? '');
        setSignalwireSpaceUrl(normalizedIntegrations.signalwireSpaceUrl ?? '');
        setSignalwireProjectId(normalizedIntegrations.signalwireProjectId ?? '');
        setSignalwireApiToken(normalizedIntegrations.signalwireApiToken ?? '');
        setSignalwirePhoneNumber(normalizedIntegrations.signalwirePhoneNumber ?? '');
        setRoutingMode(normalizedIntegrations.routingMode);
        setSavedRoutingMode(normalizedIntegrations.routingMode);
        setProviderRouterCutoverAcknowledged(
          normalizedIntegrations.routingMode === 'provider_router',
        );
        setVoicePrimaryProvider(normalizedIntegrations.voicePrimaryProvider);
        setVoiceFailoverProviders(normalizedIntegrations.voiceFailoverProviders);
        setSmsPrimaryProvider(normalizedIntegrations.smsPrimaryProvider);
        setSmsFailoverProviders(normalizedIntegrations.smsFailoverProviders);
        setDefaultNumberOwnerProvider(normalizedIntegrations.defaultNumberOwnerProvider);
        setNumberInventory(normalizedIntegrations.numberInventory ?? []);
        setRoutingReadiness(normalizedIntegrations.routingReadiness ?? null);
        setRoutingWarnings([]);
        setInboundVoiceSettings(inboundVoiceData);
        setVoiceMediaAssets(voiceMediaData);

        setTrainingEnabled(training.settings.enabled);
        setTrainingLookbackDays(training.settings.lookbackDays);
        setTrainingMinSamples(training.settings.minSamples);
        setTrainingNegativeThreshold(training.settings.negativeRateThreshold);
        setTrainingPositiveThreshold(training.settings.positiveRateThreshold);
        setTrainingTemperatureStep(training.settings.temperatureStep);
        setTrainingMaxTokensStep(training.settings.maxTokensStep);
        setTrainingAutoApplyOnCallEnd(training.settings.autoApplyOnCallEnd);
        setTrainingStats(training.stats);
        setTrainingRecommendation(training.recommendation);
        setRecentFeedback(training.recentFeedback);

        const storedTypographyPreference = preferences.find(
          (entry) => entry.key === TYPOGRAPHY_SCALE_PREFERENCE_KEY,
        )?.value;
        if (storedTypographyPreference !== undefined) {
          setTypographyScale(normalizeTypographyScale(storedTypographyPreference));
        }
      } catch (e) {
        console.error('Failed to load settings', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  useEffect(() => {
    applyTypographyScale(typographyScale);
    setStoredTypographyScale(typographyScale);
  }, [typographyScale]);

  useEffect(() => {
    setVoiceFailoverProviders((current) =>
      sanitizeFailoverProviders(current, voicePrimaryProvider),
    );
  }, [voicePrimaryProvider]);

  useEffect(() => {
    setSmsFailoverProviders((current) => sanitizeFailoverProviders(current, smsPrimaryProvider));
  }, [smsPrimaryProvider]);

  const normalizedTemperature = useMemo(
    () => normalizeTemperatureInput(temperatureSlider),
    [temperatureSlider],
  );
  const temperatureEstimate = useMemo(
    () => getTemperatureEstimate(normalizedTemperature),
    [normalizedTemperature],
  );
  const maxTokensEstimate = useMemo(() => getMaxTokensEstimate(maxTokens), [maxTokens]);

  const rankedTextModels = useMemo(
    () => rankModels([summaryModel, highlightModel, actionItemsModel, ...textModels], 'text'),
    [actionItemsModel, highlightModel, summaryModel, textModels],
  );
  const rankedTranscriptModels = useMemo(
    () => rankModels([transcriptModel, ...transcriptModels], 'transcript'),
    [transcriptModel, transcriptModels],
  );
  const activePromptMeta = useMemo(
    () => PROMPT_EDITOR_TABS.find((tab) => tab.key === activePromptKey) ?? PROMPT_EDITOR_TABS[0],
    [activePromptKey],
  );
  const promptValues = useMemo(
    () => ({
      summary: summaryPrompt,
      highlights: highlightsPrompt,
      actionItems: actionItemsPrompt,
    }),
    [actionItemsPrompt, highlightsPrompt, summaryPrompt],
  );
  const activePromptValue = promptValues[activePromptKey];
  const ragTelemetryAlerts = useMemo(() => buildRagTelemetryAlerts(ragTelemetry), [ragTelemetry]);
  const ragTelemetryHealth = useMemo(
    () => resolveRagTelemetryHealth(ragTelemetryAlerts),
    [ragTelemetryAlerts],
  );
  const realtimeTelemetryAlerts = useMemo(
    () => buildRealtimeTelemetryAlerts(realtimeTelemetry),
    [realtimeTelemetry],
  );
  const realtimeIngressRecoveryReasons = useMemo(
    () => buildRealtimeIngressRecoveryReasons(realtimeTelemetry).slice(0, 3),
    [realtimeTelemetry],
  );
  const realtimeIngressRecoveryActions = useMemo(
    () => buildRealtimeIngressRecoveryActions(realtimeTelemetry),
    [realtimeTelemetry],
  );
  const realtimeTelemetryHealth = useMemo(
    () => resolveRealtimeTelemetryHealth(realtimeTelemetryAlerts),
    [realtimeTelemetryAlerts],
  );
  const timezoneOptions = useMemo(() => {
    const options = new Set<string>(COMMON_TIMEZONES);
    if (inboundVoiceSettings.timezone.trim()) {
      options.add(inboundVoiceSettings.timezone.trim());
    }
    return Array.from(options);
  }, [inboundVoiceSettings.timezone]);
  const voiceFailoverCandidates = useMemo(
    () => ROUTING_PROVIDER_OPTIONS.filter((provider) => provider !== voicePrimaryProvider),
    [voicePrimaryProvider],
  );
  const smsFailoverCandidates = useMemo(
    () => ROUTING_PROVIDER_OPTIONS.filter((provider) => provider !== smsPrimaryProvider),
    [smsPrimaryProvider],
  );
  const activeRoutingReadiness = useMemo(
    () =>
      buildRoutingReadinessPreview({
        routingMode,
        voicePrimaryProvider,
        voiceFailoverProviders,
        smsPrimaryProvider,
        smsFailoverProviders,
        defaultNumberOwnerProvider,
        twilioAccountSid: twilioSid,
        twilioAuthToken: twilioToken,
        twilioApiKey,
        twilioApiSecret,
        twilioPhoneNumber,
        telnyxApiKey,
        telnyxPhoneNumber,
        signalwireSpaceUrl,
        signalwireProjectId,
        signalwireApiToken,
        signalwirePhoneNumber,
        baselineProviders: routingReadiness?.providers ?? null,
      }),
    [
      defaultNumberOwnerProvider,
      routingMode,
      routingReadiness,
      signalwireApiToken,
      smsFailoverProviders,
      smsPrimaryProvider,
      signalwirePhoneNumber,
      signalwireProjectId,
      signalwireSpaceUrl,
      telnyxApiKey,
      telnyxPhoneNumber,
      twilioApiKey,
      twilioApiSecret,
      twilioPhoneNumber,
      twilioSid,
      twilioToken,
      voiceFailoverProviders,
      voicePrimaryProvider,
    ],
  );
  const requiresProviderRouterCutoverAcknowledgement =
    routingMode === 'provider_router' && savedRoutingMode !== 'provider_router';
  const voiceMediaByPurpose = useMemo(() => {
    return voiceMediaAssets.reduce(
      (acc, asset) => {
        if (asset.purpose === 'welcome') {
          acc.welcome.push({ value: asset.id, label: asset.label });
        } else if (asset.purpose === 'after_hours') {
          acc.afterHours.push({ value: asset.id, label: asset.label });
        } else if (asset.purpose === 'voicemail') {
          acc.voicemail.push({ value: asset.id, label: asset.label });
        } else if (asset.purpose === 'recording_notice') {
          acc.recordingNotice.push({ value: asset.id, label: asset.label });
        } else if (asset.purpose === 'generic') {
          const option = { value: asset.id, label: asset.label };
          acc.afterHours.push(option);
          acc.voicemail.push(option);
          acc.recordingNotice.push(option);
        }

        return acc;
      },
      {
        welcome: [] as { value: string; label: string }[],
        afterHours: [] as { value: string; label: string }[],
        voicemail: [] as { value: string; label: string }[],
        recordingNotice: [] as { value: string; label: string }[],
      },
    );
  }, [voiceMediaAssets]);
  const activeTopLevelTabMeta = useMemo(
    () =>
      SETTINGS_TOP_LEVEL_TABS.find((tab) => tab.key === activeTopLevelTab) ??
      SETTINGS_TOP_LEVEL_TABS[0],
    [activeTopLevelTab],
  );
  const activeAiViewMeta = useMemo(
    () => AI_SETTINGS_VIEWS.find((tab) => tab.key === aiSettingsView) ?? AI_SETTINGS_VIEWS[0],
    [aiSettingsView],
  );
  const providerWebhookReadiness = useMemo(
    () =>
      PROVIDER_WEBHOOK_READINESS_ROWS.map((row) => ({
        ...row,
        absoluteUrl:
          typeof window === 'undefined' ? row.path : new URL(row.path, window.location.origin).href,
      })),
    [],
  );

  const handlePromptChange = (key: PromptKey, value: string) => {
    if (key === 'summary') {
      setSummaryPrompt(value);
      return;
    }
    if (key === 'highlights') {
      setHighlightsPrompt(value);
      return;
    }
    setActionItemsPrompt(value);
  };

  const handlePromptReset = (key: PromptKey) => {
    handlePromptChange(key, DEFAULT_PROMPTS[key]);
  };

  const handleAdjustTypography = (key: TypographyScaleKey, delta: number) => {
    setTypographyScale((current) => adjustTypographyScale(current, key, delta));
  };
  const updateInboundVoiceSettings = (next: Partial<InboundVoiceSettings>) => {
    setInboundVoiceSettings((current) => ({
      ...current,
      ...next,
    }));
  };
  const updateScheduleWindow = (day: Weekday, patch: Partial<InboundVoiceScheduleWindow>) => {
    setInboundVoiceSettings((current) => ({
      ...current,
      schedule: current.schedule.map((window) =>
        window.day === day
          ? {
              ...window,
              ...patch,
            }
          : window,
      ),
    }));
  };
  const addNumberInventoryRow = () => {
    setNumberInventory((current) => [
      ...current,
      {
        phoneNumber: '',
        ownerProvider: defaultNumberOwnerProvider,
        smsProvider: defaultNumberOwnerProvider,
        voiceProvider: defaultNumberOwnerProvider,
        migrationState: 'staged',
      },
    ]);
  };
  const updateNumberInventoryRow = (
    index: number,
    patch: Partial<TelephonyNumberInventoryEntry>,
  ) => {
    setNumberInventory((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              ...patch,
            }
          : row,
      ),
    );
  };
  const removeNumberInventoryRow = (index: number) => {
    setNumberInventory((current) => current.filter((_, rowIndex) => rowIndex !== index));
  };

  const refreshTrainingOverview = async () => {
    const { data } = await api.getAiTrainingOverview();
    setTrainingStats(data.stats);
    setTrainingRecommendation(data.recommendation);
    setRecentFeedback(data.recentFeedback);
  };

  const refreshRagTelemetry = async () => {
    setLoadingRagTelemetry(true);
    try {
      const lookbackDays = normalizeTelemetryLookbackDays(ragTelemetryLookbackDays);
      const { data } = await api.getRagTelemetry(lookbackDays);
      setRagTelemetry(data);
      setRagTelemetryLookbackDays(normalizeTelemetryLookbackDays(data.lookbackDays));
    } catch (error) {
      console.error('Failed to refresh RAG telemetry', error);
    } finally {
      setLoadingRagTelemetry(false);
    }
  };

  const refreshRealtimeTelemetry = async () => {
    setLoadingRealtimeTelemetry(true);
    try {
      const lookbackDays = normalizeTelemetryLookbackDays(realtimeTelemetryLookbackDays);
      const { data } = await api.getRealtimeTelemetry(lookbackDays);
      setRealtimeTelemetry(data);
      setRealtimeTelemetryLookbackDays(normalizeTelemetryLookbackDays(data.lookbackDays));
    } catch (error) {
      console.error('Failed to refresh realtime transcript telemetry', error);
    } finally {
      setLoadingRealtimeTelemetry(false);
    }
  };

  const refreshVoiceMediaAssets = async () => {
    const { data } = await api.getVoiceMediaAssets();
    setVoiceMediaAssets(data);
  };

  const handleUploadVoiceMedia = async () => {
    setVoiceMediaUploadError(null);
    setVoiceMediaUploadSuccess(null);

    const label = voiceMediaUploadLabel.trim();
    if (!label) {
      setVoiceMediaUploadError('Voice media label is required.');
      return;
    }

    if (!voiceMediaUploadFile) {
      setVoiceMediaUploadError('Select an .mp3 or .wav file to upload.');
      return;
    }

    const normalizedName = voiceMediaUploadFile.name.toLowerCase();
    const hasSupportedExtension =
      normalizedName.endsWith('.mp3') || normalizedName.endsWith('.wav');
    const normalizedType = voiceMediaUploadFile.type.trim().toLowerCase();
    const hasSupportedMimeType =
      normalizedType.length === 0 || VOICE_MEDIA_UPLOAD_ALLOWED_MIME_TYPES.has(normalizedType);
    if (!hasSupportedExtension || !hasSupportedMimeType) {
      setVoiceMediaUploadError('Only .mp3 and .wav files are supported.');
      return;
    }

    setUploadingVoiceMedia(true);
    try {
      await api.uploadVoiceMediaAsset({
        label,
        purpose: voiceMediaUploadPurpose,
        file: voiceMediaUploadFile,
      });
      await refreshVoiceMediaAssets();
      setVoiceMediaUploadLabel('');
      setVoiceMediaUploadPurpose('generic');
      setVoiceMediaUploadFile(null);
      setVoiceMediaUploadSuccess('Voice media uploaded and ready to select.');
    } catch (error) {
      console.error('Failed to upload voice media', error);
      setVoiceMediaUploadError(
        error instanceof Error ? error.message : 'Failed to upload voice media.',
      );
    } finally {
      setUploadingVoiceMedia(false);
    }
  };

  const handleSave = async () => {
    setSaveErrorMessage(null);
    setSaving(true);
    try {
      if (activeRoutingReadiness.blockingIssues.length > 0) {
        setSaveErrorMessage(
          `Routing policy validation failed - ${activeRoutingReadiness.blockingIssues.join('; ')}`,
        );
        return;
      }
      if (requiresProviderRouterCutoverAcknowledgement && !providerRouterCutoverAcknowledged) {
        setSaveErrorMessage(
          'Confirm provider-router cutover before saving. Acknowledge that New routing will become active and rollback remains available via Routing Mode.',
        );
        return;
      }

      const ragPayload: RagSettings = normalizeRagSettingsForSave({
        enabled: ragEnabled,
        manualOnly: ragManualOnly,
        topK: ragTopK,
        maxSuggestions: ragMaxSuggestions,
        minConfidence: ragMinConfidence,
        maxContextChars: ragMaxContextChars,
        embeddingModel: ragEmbeddingModel,
        generationModel: ragGenerationModel,
        promptTemplate: ragPromptTemplate,
      });

      const [, integrationSaveResult] = await Promise.all([
        api.saveAiSettings({
          modelParams: {
            temperature: normalizedTemperature,
            maxTokens,
          },
          models: {
            summaryModel,
            highlightModel,
            actionItemsModel,
            transcriptModel,
          },
          prompts: {
            summary: summaryPrompt,
            highlights: highlightsPrompt,
            actionItems: actionItemsPrompt,
          },
          features: {
            autoSummary,
            autoHighlights,
            autoActionItems,
          },
        }),
        api.saveTeamIntegrations({
          twilioAccountSid: twilioSid,
          twilioAuthToken: twilioToken,
          twilioApiKey,
          twilioApiSecret,
          twilioPhoneNumber,
          telnyxApiKey,
          telnyxMessagingProfileId,
          telnyxPhoneNumber,
          signalwireSpaceUrl,
          signalwireProjectId,
          signalwireApiToken,
          signalwirePhoneNumber,
          routingMode,
          voicePrimaryProvider,
          voiceFailoverProviders,
          smsPrimaryProvider,
          smsFailoverProviders,
          defaultNumberOwnerProvider,
          numberInventory,
        }),
        api.saveMyPreference({
          key: TYPOGRAPHY_SCALE_PREFERENCE_KEY,
          value: typographyScale,
        }),
        api.saveRagSettings(ragPayload),
        api.saveInboundVoiceSettings(inboundVoiceSettings),
      ]);
      const savedRoutingPolicy = integrationSaveResult?.telephonyRouting;
      let savedDefaultNumberOwnerProvider: VoiceProvider = defaultNumberOwnerProvider;
      if (savedRoutingPolicy) {
        const normalizedSavedIntegrations = normalizeTeamIntegrations(savedRoutingPolicy);
        setRoutingMode(normalizedSavedIntegrations.routingMode);
        setSavedRoutingMode(normalizedSavedIntegrations.routingMode);
        setProviderRouterCutoverAcknowledged(
          normalizedSavedIntegrations.routingMode === 'provider_router',
        );
        setVoicePrimaryProvider(normalizedSavedIntegrations.voicePrimaryProvider);
        setVoiceFailoverProviders(normalizedSavedIntegrations.voiceFailoverProviders);
        setSmsPrimaryProvider(normalizedSavedIntegrations.smsPrimaryProvider);
        setSmsFailoverProviders(normalizedSavedIntegrations.smsFailoverProviders);
        setTelnyxApiKey(normalizedSavedIntegrations.telnyxApiKey ?? '');
        setTelnyxMessagingProfileId(normalizedSavedIntegrations.telnyxMessagingProfileId ?? '');
        setTelnyxPhoneNumber(normalizedSavedIntegrations.telnyxPhoneNumber ?? '');
        setSignalwireSpaceUrl(normalizedSavedIntegrations.signalwireSpaceUrl ?? '');
        setSignalwireProjectId(normalizedSavedIntegrations.signalwireProjectId ?? '');
        setSignalwireApiToken(normalizedSavedIntegrations.signalwireApiToken ?? '');
        setSignalwirePhoneNumber(normalizedSavedIntegrations.signalwirePhoneNumber ?? '');
        savedDefaultNumberOwnerProvider = normalizedSavedIntegrations.defaultNumberOwnerProvider;
        setDefaultNumberOwnerProvider(normalizedSavedIntegrations.defaultNumberOwnerProvider);
        setRoutingReadiness(normalizedSavedIntegrations.routingReadiness ?? null);
      }
      if (integrationSaveResult?.numberInventory !== undefined) {
        setNumberInventory(
          normalizeNumberInventory(
            integrationSaveResult.numberInventory,
            savedDefaultNumberOwnerProvider,
          ),
        );
      }
      const warnings = integrationSaveResult?.routingWarnings ?? [];
      setRoutingWarnings(warnings);
      await Promise.all([refreshRagTelemetry(), refreshRealtimeTelemetry()]);
      if (warnings.length > 0) {
        alert(`Settings saved with routing warnings: ${warnings.join(' ')}`);
      } else {
        alert('Settings saved successfully!');
      }
    } catch (e) {
      console.error('Failed to save settings', e);
      setSaveErrorMessage(formatSettingsSaveError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTraining = async () => {
    setSavingTraining(true);
    try {
      const payload: Partial<AiTrainingSettings> = {
        enabled: trainingEnabled,
        lookbackDays: Math.max(1, Math.round(trainingLookbackDays || 1)),
        minSamples: Math.max(1, Math.round(trainingMinSamples || 1)),
        negativeRateThreshold: Math.max(0, Math.min(1, trainingNegativeThreshold || 0)),
        positiveRateThreshold: Math.max(0, Math.min(1, trainingPositiveThreshold || 0)),
        temperatureStep: Math.max(0.01, trainingTemperatureStep || 0.01),
        maxTokensStep: Math.max(1, Math.round(trainingMaxTokensStep || 1)),
        autoApplyOnCallEnd: trainingAutoApplyOnCallEnd,
      };

      const { data } = await api.saveAiTrainingSettings(payload);
      await api.saveMyPreference({
        key: TYPOGRAPHY_SCALE_PREFERENCE_KEY,
        value: typographyScale,
      });
      setTrainingEnabled(data.enabled);
      setTrainingLookbackDays(data.lookbackDays);
      setTrainingMinSamples(data.minSamples);
      setTrainingNegativeThreshold(data.negativeRateThreshold);
      setTrainingPositiveThreshold(data.positiveRateThreshold);
      setTrainingTemperatureStep(data.temperatureStep);
      setTrainingMaxTokensStep(data.maxTokensStep);
      setTrainingAutoApplyOnCallEnd(data.autoApplyOnCallEnd);
      await refreshTrainingOverview();
      alert('Fine-tuning settings saved.');
    } catch (error) {
      console.error('Failed to save fine-tuning settings', error);
      alert('Failed to save fine-tuning settings.');
    } finally {
      setSavingTraining(false);
    }
  };

  const handleApplyTraining = async () => {
    setApplyingTraining(true);
    try {
      const { data } = await api.applyAiTrainingRecommendation();
      setTrainingRecommendation(data);
      await refreshTrainingOverview();

      const refreshedAi = await api.getAiSettings();
      setTemperatureSlider(Math.round(refreshedAi.data.settings.modelParams.temperature * 100));
      setMaxTokens(refreshedAi.data.settings.modelParams.maxTokens);

      if (data.hasChanges) {
        alert('Applied fine-tuning recommendation to model parameters.');
      } else {
        alert('No recommendation changes to apply.');
      }
    } catch (error) {
      console.error('Failed to apply fine-tuning recommendation', error);
      alert('Failed to apply fine-tuning recommendation.');
    } finally {
      setApplyingTraining(false);
    }
  };

  const handlePushSecrets = async () => {
    if (
      !twilioSid &&
      !twilioToken &&
      !twilioPhoneNumber &&
      !telnyxApiKey &&
      !telnyxPhoneNumber &&
      !signalwireSpaceUrl &&
      !signalwireProjectId &&
      !signalwireApiToken &&
      !signalwirePhoneNumber
    ) {
      alert('Provide at least one provider credential value before pushing secrets to Cloudflare.');
      return;
    }

    setPushingSecrets(true);
    try {
      const secrets: Record<string, string> = {};
      if (twilioSid) secrets.TWILIO_ACCOUNT_SID = twilioSid;
      if (twilioToken) secrets.TWILIO_AUTH_TOKEN = twilioToken;
      if (twilioApiKey) secrets.TWILIO_API_KEY = twilioApiKey;
      if (twilioApiSecret) secrets.TWILIO_API_SECRET = twilioApiSecret;
      if (twilioPhoneNumber) secrets.TWILIO_PHONE_NUMBER = twilioPhoneNumber;
      if (telnyxApiKey) secrets.TELNYX_API_KEY = telnyxApiKey;
      if (telnyxMessagingProfileId) {
        secrets.TELNYX_MESSAGING_PROFILE_ID = telnyxMessagingProfileId;
      }
      if (telnyxPhoneNumber) secrets.TELNYX_PHONE_NUMBER = telnyxPhoneNumber;
      if (signalwireSpaceUrl) secrets.SIGNALWIRE_SPACE_URL = signalwireSpaceUrl;
      if (signalwireProjectId) secrets.SIGNALWIRE_PROJECT_ID = signalwireProjectId;
      if (signalwireApiToken) secrets.SIGNALWIRE_API_TOKEN = signalwireApiToken;
      if (signalwirePhoneNumber) secrets.SIGNALWIRE_PHONE_NUMBER = signalwirePhoneNumber;
      await api.pushSystemSecrets(secrets);
      alert('Secrets successfully pushed to Cloudflare Secrets Store and worker restarted!');
    } catch (e) {
      console.error('Failed to push secrets', e);
      alert(
        'Failed to push secrets to Cloudflare. Ensure CLOUDFLARE_API_TOKEN is set in the worker environment bindings.',
      );
    } finally {
      setPushingSecrets(false);
    }
  };

  if (loading) return <div className="text-muted-foreground">Loading settings...</div>;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <AuroraBackground className="rounded-2xl border border-border bg-card/90 p-6">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-2xl font-semibold text-foreground">Settings</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {activeTopLevelTabMeta.description}
            </p>
            <div className="mt-3 inline-flex rounded-xl border border-border bg-background/70 p-1">
              {SETTINGS_TOP_LEVEL_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTopLevelTab(tab.key)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    activeTopLevelTab === tab.key
                      ? 'bg-brand-500 text-white'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {activeTopLevelTab === 'ai' ? (
              <div className="mt-3 inline-flex rounded-xl border border-border bg-background/70 p-1">
                {AI_SETTINGS_VIEWS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setAiSettingsView(tab.key)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                      aiSettingsView === tab.key
                        ? 'bg-brand-500 text-white'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            ) : null}
            {activeTopLevelTab === 'ai' ? (
              <p className="mt-2 text-xs text-muted-foreground">{activeAiViewMeta.description}</p>
            ) : null}
          </div>
          {activeTopLevelTab === 'ai' && aiSettingsView === 'training' ? (
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleSaveTraining}
                disabled={savingTraining}
                className="bg-brand-500 text-white transition-colors hover:bg-brand-400"
              >
                {savingTraining ? 'Saving...' : 'Save Fine-tuning'}
              </Button>
              <Button
                onClick={handleApplyTraining}
                disabled={applyingTraining || !trainingRecommendation.hasChanges}
                className="border border-brand-500/40 bg-background text-brand-700 transition-colors hover:bg-brand-500/10 hover:text-brand-700 dark:text-brand-300"
              >
                {applyingTraining ? 'Applying...' : 'Apply Recommendation'}
              </Button>
            </div>
          ) : activeTopLevelTab === 'ai' && aiSettingsView === 'telemetry' ? (
            <span className="rounded-full border border-border bg-background/70 px-3 py-1 text-xs text-muted-foreground">
              Telemetry is read-only. Use in-panel refresh controls to update data.
            </span>
          ) : (
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-brand-500 text-white transition-colors hover:bg-brand-400"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          )}
        </header>
      </AuroraBackground>

      {saveErrorMessage ? (
        <section
          role="alert"
          className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-700 dark:text-rose-300"
        >
          <p className="font-medium">Settings save blocked</p>
          <p className="mt-1">{saveErrorMessage}</p>
        </section>
      ) : null}

      {activeTopLevelTab === 'appearance' ? (
        <>
          <section className="rounded-xl border border-border bg-card p-6">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-foreground">Theme Mode</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Choose whether the workspace follows the system theme or stays pinned to light or
                dark mode.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-[minmax(0,240px)_1fr]">
              <div className="rounded-lg border border-border bg-background/70 p-3">
                <label htmlFor="theme-mode" className="block text-xs text-muted-foreground">
                  Theme mode
                </label>
                <select
                  id="theme-mode"
                  value={themeMode}
                  onChange={(event) => setThemeMode(event.target.value as typeof themeMode)}
                  className="mt-2 h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:border-brand-500 focus:outline-none"
                >
                  <option value="auto">Auto</option>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </select>
              </div>

              <div className="rounded-lg border border-border bg-background/70 p-3 text-sm">
                <p className="font-medium text-foreground">Current theme state</p>
                <p className="mt-2 text-muted-foreground">
                  Stored mode: <span className="font-medium text-foreground">{themeMode}</span>
                </p>
                <p className="mt-1 text-muted-foreground">
                  Active palette:{' '}
                  <span className="font-medium text-foreground">{resolvedTheme}</span>
                </p>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-border bg-card p-4 sm:p-5">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
              <div>
                <h3 className="text-lg font-medium text-foreground">Typography Scale</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Adjust title, subtitle, body, label, and caption sizes across the app. Changes
                  apply immediately and are saved with settings.
                </p>
              </div>
              <span className="rounded-full bg-background px-2 py-0.5 text-xs text-muted-foreground">
                Step range -4 to +4
              </span>
            </div>
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {TYPOGRAPHY_CONTROL_ORDER.map((control) => (
                <div
                  key={control.key}
                  className="rounded-lg border border-border bg-background/70 px-3 py-2"
                >
                  <p className="text-sm font-medium text-foreground">{control.label}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{control.description}</p>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => handleAdjustTypography(control.key, -1)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-card text-sm text-foreground transition-colors hover:bg-muted"
                      aria-label={`Decrease ${control.label} font size`}
                    >
                      -
                    </button>
                    <span className="min-w-[2.5rem] text-center text-xs font-medium text-foreground">
                      {typographyScale[control.key] > 0
                        ? `+${typographyScale[control.key]}`
                        : typographyScale[control.key]}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleAdjustTypography(control.key, 1)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-card text-sm text-foreground transition-colors hover:bg-muted"
                      aria-label={`Increase ${control.label} font size`}
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      ) : null}

      {activeTopLevelTab === 'business' ? (
        <section className="rounded-xl border border-border bg-card p-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-foreground">Inbound Voice Routing</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Configure provider-neutral working hours, greetings, and voicemail behavior for
              inbound calls.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-lg border border-border bg-background/70 p-3">
              <label htmlFor="inbound-timezone" className="block text-xs text-muted-foreground">
                Team timezone
              </label>
              <select
                id="inbound-timezone"
                value={inboundVoiceSettings.timezone}
                onChange={(event) => updateInboundVoiceSettings({ timezone: event.target.value })}
                className="mt-2 h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:border-brand-500 focus:outline-none"
              >
                {timezoneOptions.map((timezone) => (
                  <option key={timezone} value={timezone}>
                    {timezone}
                  </option>
                ))}
              </select>
            </div>

            <div className="rounded-lg border border-border bg-background/70 p-3">
              <label htmlFor="inbound-ring-timeout" className="block text-xs text-muted-foreground">
                Ring timeout (seconds)
              </label>
              <input
                id="inbound-ring-timeout"
                type="number"
                min={5}
                max={120}
                value={String(inboundVoiceSettings.ringTimeoutSeconds)}
                onChange={(event) =>
                  updateInboundVoiceSettings({
                    ringTimeoutSeconds: Number.parseInt(event.target.value || '0', 10),
                  })
                }
                className="mt-2 h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:border-brand-500 focus:outline-none"
              />
            </div>

            <div className="rounded-lg border border-border bg-background/70 p-3">
              <label
                htmlFor="inbound-unavailable-behavior"
                className="block text-xs text-muted-foreground"
              >
                Offline behavior
              </label>
              <select
                id="inbound-unavailable-behavior"
                value={inboundVoiceSettings.unavailableBehavior}
                onChange={(event) =>
                  updateInboundVoiceSettings({
                    unavailableBehavior: event.target
                      .value as InboundVoiceSettings['unavailableBehavior'],
                  })
                }
                className="mt-2 h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:border-brand-500 focus:outline-none"
              >
                <option value="voicemail">Route to voicemail</option>
                <option value="hangup">Hang up after greeting</option>
              </select>
            </div>

            <div className="rounded-lg border border-border bg-background/70 p-3">
              <label htmlFor="welcome-asset" className="block text-xs text-muted-foreground">
                Welcome greeting asset
              </label>
              <select
                id="welcome-asset"
                value={inboundVoiceSettings.welcomeGreetingAssetId ?? ''}
                onChange={(event) =>
                  updateInboundVoiceSettings({
                    welcomeGreetingAssetId: event.target.value || null,
                  })
                }
                className="mt-2 h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:border-brand-500 focus:outline-none"
              >
                <option value="">None</option>
                {voiceMediaByPurpose.welcome.map((asset) => (
                  <option key={asset.value} value={asset.value}>
                    {asset.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <label className="flex items-center gap-2 rounded-lg border border-border bg-background/70 p-3 text-sm text-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 accent-brand-500"
                checked={inboundVoiceSettings.welcomeGreetingEnabled}
                onChange={(event) =>
                  updateInboundVoiceSettings({ welcomeGreetingEnabled: event.target.checked })
                }
                aria-label="Enable welcome recording"
              />
              Enable welcome recording
            </label>
            <label className="flex items-center gap-2 rounded-lg border border-border bg-background/70 p-3 text-sm text-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 accent-brand-500"
                checked={inboundVoiceSettings.voicemailEnabled}
                onChange={(event) =>
                  updateInboundVoiceSettings({ voicemailEnabled: event.target.checked })
                }
                aria-label="Enable voicemail"
              />
              Enable voicemail
            </label>

            <div className="rounded-lg border border-border bg-background/70 p-3">
              <label htmlFor="after-hours-asset" className="block text-xs text-muted-foreground">
                After-hours greeting asset
              </label>
              <select
                id="after-hours-asset"
                value={inboundVoiceSettings.afterHoursGreetingAssetId ?? ''}
                onChange={(event) =>
                  updateInboundVoiceSettings({
                    afterHoursGreetingAssetId: event.target.value || null,
                  })
                }
                className="mt-2 h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:border-brand-500 focus:outline-none"
              >
                <option value="">None</option>
                {voiceMediaByPurpose.afterHours.map((asset) => (
                  <option key={asset.value} value={asset.value}>
                    {asset.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="rounded-lg border border-border bg-background/70 p-3">
              <label htmlFor="voicemail-asset" className="block text-xs text-muted-foreground">
                Voicemail greeting asset
              </label>
              <select
                id="voicemail-asset"
                value={inboundVoiceSettings.voicemailGreetingAssetId ?? ''}
                onChange={(event) =>
                  updateInboundVoiceSettings({
                    voicemailGreetingAssetId: event.target.value || null,
                  })
                }
                className="mt-2 h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:border-brand-500 focus:outline-none"
              >
                <option value="">None</option>
                {voiceMediaByPurpose.voicemail.map((asset) => (
                  <option key={asset.value} value={asset.value}>
                    {asset.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="rounded-lg border border-border bg-background/70 p-3 md:col-span-2 xl:col-span-4">
              <label
                htmlFor="recording-notice-asset"
                className="block text-xs text-muted-foreground"
              >
                Recording notice asset
              </label>
              <select
                id="recording-notice-asset"
                value={inboundVoiceSettings.recordingNoticeAssetId ?? ''}
                onChange={(event) =>
                  updateInboundVoiceSettings({
                    recordingNoticeAssetId: event.target.value || null,
                  })
                }
                className="mt-2 h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:border-brand-500 focus:outline-none"
              >
                <option value="">None</option>
                {voiceMediaByPurpose.recordingNotice.map((asset) => (
                  <option key={asset.value} value={asset.value}>
                    {asset.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-5 rounded-xl border border-border bg-background/70 p-4">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
              <div>
                <h4 className="text-sm font-semibold text-foreground">Voice media library</h4>
                <p className="mt-1 text-xs text-muted-foreground">
                  Upload <code>.mp3</code> or <code>.wav</code> files for welcome, after-hours,
                  voicemail, and recording notice playback.
                </p>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-lg border border-border bg-card p-3 md:col-span-2">
                <label
                  htmlFor="voice-media-upload-label"
                  className="block text-xs text-muted-foreground"
                >
                  Voice media label
                </label>
                <Input
                  id="voice-media-upload-label"
                  value={voiceMediaUploadLabel}
                  onChange={(event) => setVoiceMediaUploadLabel(event.target.value)}
                  className="mt-2 bg-background"
                  placeholder="Example: Welcome Greeting v2"
                />
              </div>

              <div className="rounded-lg border border-border bg-card p-3">
                <label
                  htmlFor="voice-media-upload-purpose"
                  className="block text-xs text-muted-foreground"
                >
                  Voice media purpose
                </label>
                <select
                  id="voice-media-upload-purpose"
                  value={voiceMediaUploadPurpose}
                  onChange={(event) =>
                    setVoiceMediaUploadPurpose(event.target.value as VoiceMediaAssetPurpose)
                  }
                  className="mt-2 h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:border-brand-500 focus:outline-none"
                >
                  <option value="welcome">Welcome</option>
                  <option value="after_hours">After Hours</option>
                  <option value="voicemail">Voicemail</option>
                  <option value="recording_notice">Recording Notice</option>
                  <option value="generic">Generic</option>
                </select>
              </div>

              <div className="rounded-lg border border-border bg-card p-3">
                <label
                  htmlFor="voice-media-upload-file"
                  className="block text-xs text-muted-foreground"
                >
                  Voice media file (.mp3/.wav)
                </label>
                <input
                  id="voice-media-upload-file"
                  type="file"
                  accept=".mp3,.wav,audio/mpeg,audio/wav"
                  className="mt-2 block w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-brand-500/15 file:px-2.5 file:py-1 file:text-xs file:font-medium file:text-brand-700 hover:file:bg-brand-500/20 dark:file:text-brand-300"
                  onChange={(event) => {
                    const nextFile = event.target.files?.[0] ?? null;
                    setVoiceMediaUploadFile(nextFile);
                    setVoiceMediaUploadError(null);
                    setVoiceMediaUploadSuccess(null);
                  }}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {voiceMediaUploadFile ? voiceMediaUploadFile.name : 'No file selected.'}
                </p>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                onClick={handleUploadVoiceMedia}
                disabled={uploadingVoiceMedia}
                className="bg-brand-500 text-white transition-colors hover:bg-brand-400 disabled:opacity-50"
              >
                {uploadingVoiceMedia ? 'Uploading...' : 'Upload Voice Media'}
              </Button>
              {voiceMediaUploadError ? (
                <p className="text-xs text-red-500 dark:text-red-300">{voiceMediaUploadError}</p>
              ) : null}
              {voiceMediaUploadSuccess ? (
                <p className="text-xs text-emerald-600 dark:text-emerald-300">
                  {voiceMediaUploadSuccess}
                </p>
              ) : null}
            </div>
          </div>

          <div className="mt-5 rounded-xl border border-border bg-background/70 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h4 className="text-sm font-semibold text-foreground">Working hours schedule</h4>
                <p className="mt-1 text-xs text-muted-foreground">
                  Eligibility depends on both this schedule and live agent availability.
                </p>
              </div>
            </div>
            <div className="space-y-3">
              {inboundVoiceSettings.schedule.map((window) => (
                <div
                  key={window.day}
                  className="grid gap-3 rounded-lg border border-border bg-card p-3 md:grid-cols-[140px,120px,1fr,1fr]"
                >
                  <div className="flex items-center justify-between gap-2 md:block">
                    <p className="text-sm font-medium text-foreground">
                      {formatWeekdayLabel(window.day)}
                    </p>
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-brand-500"
                        checked={window.enabled}
                        onChange={(event) =>
                          updateScheduleWindow(window.day, {
                            enabled: event.target.checked,
                            startTime: event.target.checked ? (window.startTime ?? '09:00') : null,
                            endTime: event.target.checked ? (window.endTime ?? '17:00') : null,
                          })
                        }
                        aria-label={`${formatWeekdayLabel(window.day)} enabled`}
                      />
                      Enabled
                    </label>
                  </div>

                  <div className="md:col-span-3 grid gap-3 md:grid-cols-2">
                    <div>
                      <label
                        htmlFor={`${window.day}-start-time`}
                        className="block text-xs text-muted-foreground"
                      >
                        {formatWeekdayLabel(window.day)} start time
                      </label>
                      <input
                        id={`${window.day}-start-time`}
                        type="time"
                        value={window.startTime ?? ''}
                        disabled={!window.enabled}
                        onChange={(event) =>
                          updateScheduleWindow(window.day, {
                            startTime: event.target.value || null,
                          })
                        }
                        className="mt-2 h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:border-brand-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`${window.day}-end-time`}
                        className="block text-xs text-muted-foreground"
                      >
                        {formatWeekdayLabel(window.day)} end time
                      </label>
                      <input
                        id={`${window.day}-end-time`}
                        type="time"
                        value={window.endTime ?? ''}
                        disabled={!window.enabled}
                        onChange={(event) =>
                          updateScheduleWindow(window.day, { endTime: event.target.value || null })
                        }
                        className="mt-2 h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:border-brand-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 grid gap-3 xl:grid-cols-2">
            <div className="rounded-xl border border-border bg-background/70 p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h4 className="text-sm font-semibold text-foreground">Operator workflow</h4>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Keep inbound voicemail execution inside <code>/voicemails</code>, then move the
                    actual customer follow-up back into Comms.
                  </p>
                </div>
                <a
                  href="#inbound-voice-runbook"
                  className="inline-flex items-center rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted"
                >
                  Open inbound voice runbook
                </a>
              </div>
              <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                <p>1. Triage new voicemail from the queue before changing routing policy.</p>
                <p>
                  2. Assign an owner, review recording/transcript state, and capture follow-up in
                  Comms.
                </p>
                <p>
                  3. Outbound voicemail notifications remain planned; this slice keeps the operator
                  workflow in-app only.
                </p>
              </div>
            </div>

            <div
              id="inbound-voice-runbook"
              className="rounded-xl border border-border bg-background/70 p-4"
            >
              <h4 className="text-sm font-semibold text-foreground">Inbound voice runbook</h4>
              <p className="mt-1 text-xs text-muted-foreground">
                Detailed operator flow is tracked in{' '}
                <code>docs/plans/2026-03-01-inbound-voice-operator-runbook.md</code>.
              </p>
              <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                <p>1. New means the voicemail still needs triage.</p>
                <p>2. Reviewed means the owner has context and is actively working the callback.</p>
                <p>
                  3. Completed means the queue task is closed and any remaining work lives in Comms.
                </p>
                <p>
                  4. Outbound voicemail notifications remain planned, and SLA/escalation semantics
                  are still unresolved follow-on work.
                </p>
              </div>
            </div>
          </div>

          {CRM_V2_ENABLED ? <CrmBusinessSettings /> : null}
        </section>
      ) : null}

      {activeTopLevelTab === 'ai' && aiSettingsView !== 'training' ? (
        <>
          <div className="grid gap-6 xl:grid-cols-2">
            <section className="rounded-xl border border-border bg-card p-6">
              <h3 className="mb-4 text-lg font-medium text-foreground">Model Parameters</h3>
              <div className="space-y-4">
                <div>
                  <label className="mb-2 flex justify-between text-sm text-muted-foreground">
                    <span>Temperature</span>
                    <span>{normalizedTemperature.toFixed(2)}</span>
                  </label>
                  <input
                    type="range"
                    className="w-full accent-brand-500"
                    min="0"
                    max="100"
                    value={temperatureSlider}
                    onChange={(event) =>
                      setTemperatureSlider(Number.parseInt(event.target.value, 10))
                    }
                  />
                </div>
                <div>
                  <label className="mb-2 flex justify-between text-sm text-muted-foreground">
                    <span>Max Tokens</span>
                    <span>{maxTokens}</span>
                  </label>
                  <input
                    type="range"
                    className="w-full accent-brand-500"
                    min="100"
                    max="2000"
                    step="10"
                    value={maxTokens}
                    onChange={(event) => setMaxTokens(Number.parseInt(event.target.value, 10))}
                  />
                </div>
              </div>

              <div className="mt-5 rounded-xl border border-brand-500/30 bg-brand-500/10 p-4">
                <h4 className="text-sm font-semibold text-brand-700 dark:text-brand-200">
                  Estimated Results
                </h4>
                <div className="mt-2 space-y-2 text-xs">
                  <p className="text-foreground">
                    Temperature effect:{' '}
                    <span className="font-medium">{temperatureEstimate.label}</span>
                  </p>
                  <p className="text-muted-foreground">{temperatureEstimate.description}</p>
                  <p className="italic text-muted-foreground">{temperatureEstimate.example}</p>
                  <p className="pt-2 text-foreground">
                    Max tokens impact:{' '}
                    <span className="font-medium">{maxTokensEstimate.label}</span>
                  </p>
                  <p className="text-muted-foreground">{maxTokensEstimate.description}</p>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-border bg-card p-6">
              <h3 className="mb-4 text-lg font-medium text-foreground">Models & Features</h3>
              <p className="mb-4 text-xs text-muted-foreground">
                Available models are fetched from API and ranked for a balanced quality/cost
                profile.
              </p>
              <div className="grid gap-4">
                <ModelSelect
                  label="Summary model"
                  value={summaryModel}
                  options={rankedTextModels}
                  onChange={setSummaryModel}
                  helper="Used for AI Summary generation."
                />
                <ModelSelect
                  label="Highlights model"
                  value={highlightModel}
                  options={rankedTextModels}
                  onChange={setHighlightModel}
                  helper="Used for Conversation Highlights extraction."
                />
                <ModelSelect
                  label="Action items model"
                  value={actionItemsModel}
                  options={rankedTextModels}
                  onChange={setActionItemsModel}
                  helper="Used for follow-up task extraction."
                />
                <ModelSelect
                  label="Transcript model"
                  value={transcriptModel}
                  options={rankedTranscriptModels}
                  onChange={setTranscriptModel}
                  helper="Used for speech-to-text transcript ingestion."
                />
              </div>

              <div className="mt-4 rounded-lg border border-border bg-background/70 p-3 text-xs text-muted-foreground">
                <p>
                  Ranking guide: higher <span className="font-medium text-foreground">quality</span>{' '}
                  improves output depth, lower{' '}
                  <span className="font-medium text-foreground">cost</span> helps keep inference
                  spend down.
                </p>
              </div>

              <div className="mt-5 space-y-3">
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-brand-500"
                    checked={autoSummary}
                    onChange={(event) => setAutoSummary(event.target.checked)}
                  />
                  Auto-generate AI summary
                </label>
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-brand-500"
                    checked={autoHighlights}
                    onChange={(event) => setAutoHighlights(event.target.checked)}
                  />
                  Auto-generate conversation highlights
                </label>
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-brand-500"
                    checked={autoActionItems}
                    onChange={(event) => setAutoActionItems(event.target.checked)}
                  />
                  Auto-generate action items
                </label>
              </div>
            </section>
          </div>

          <section className="rounded-xl border border-border bg-card p-6">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-foreground">Prompt Templates</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Edit one template at a time with focused context and reset controls.
              </p>
            </div>

            <div className="grid gap-4 xl:grid-cols-[220px_minmax(0,1fr)]">
              <div className="space-y-2">
                {PROMPT_EDITOR_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActivePromptKey(tab.key)}
                    className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${
                      activePromptKey === tab.key
                        ? 'border-brand-500/50 bg-brand-500/10'
                        : 'border-border bg-background hover:border-brand-500/40'
                    }`}
                  >
                    <p className="text-sm font-medium text-foreground">{tab.label}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {promptValues[tab.key].trim().length} chars
                    </p>
                  </button>
                ))}
              </div>

              <div className="rounded-xl border border-border bg-background/70 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {activePromptMeta.label}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {activePromptMeta.description}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => handlePromptReset(activePromptMeta.key)}
                    className="h-8 rounded-lg border border-border bg-transparent px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    Reset to default
                  </Button>
                </div>

                <TextArea
                  value={activePromptValue}
                  onChange={(event) => handlePromptChange(activePromptMeta.key, event.target.value)}
                  className="mt-3 min-h-44 text-sm"
                />

                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span>{activePromptMeta.tip}</span>
                  <span>{activePromptValue.trim().length} chars</span>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-border bg-card p-6">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-foreground">RAG Suggestions</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Configure retrieval and generation behavior used by Dialer Suggestions.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <label className="flex items-center gap-2 rounded-lg border border-border bg-background/70 p-3 text-sm text-foreground">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-brand-500"
                  checked={ragEnabled}
                  onChange={(event) => setRagEnabled(event.target.checked)}
                />
                Enable RAG suggestions
              </label>
              <label className="flex items-center gap-2 rounded-lg border border-border bg-background/70 p-3 text-sm text-foreground">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-brand-500"
                  checked={ragManualOnly}
                  onChange={(event) => setRagManualOnly(event.target.checked)}
                />
                Manual prompt required
              </label>

              <div className="rounded-lg border border-border bg-background/70 p-3">
                <label className="block text-xs text-muted-foreground">Top K evidence chunks</label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={String(ragTopK)}
                  onChange={(event) => setRagTopK(Number.parseInt(event.target.value || '1', 10))}
                  className="mt-2 bg-background"
                />
              </div>

              <div className="rounded-lg border border-border bg-background/70 p-3">
                <label className="block text-xs text-muted-foreground">Max suggestions</label>
                <Input
                  type="number"
                  min={1}
                  max={6}
                  value={String(ragMaxSuggestions)}
                  onChange={(event) =>
                    setRagMaxSuggestions(Number.parseInt(event.target.value || '1', 10))
                  }
                  className="mt-2 bg-background"
                />
              </div>

              <div className="rounded-lg border border-border bg-background/70 p-3">
                <label className="block text-xs text-muted-foreground">Minimum confidence</label>
                <Input
                  type="number"
                  min={0}
                  max={1}
                  step={0.01}
                  value={String(ragMinConfidence)}
                  onChange={(event) =>
                    setRagMinConfidence(Number.parseFloat(event.target.value || '0'))
                  }
                  className="mt-2 bg-background"
                />
              </div>

              <div className="rounded-lg border border-border bg-background/70 p-3">
                <label className="block text-xs text-muted-foreground">Max context chars</label>
                <Input
                  type="number"
                  min={800}
                  max={20000}
                  step={100}
                  value={String(ragMaxContextChars)}
                  onChange={(event) =>
                    setRagMaxContextChars(Number.parseInt(event.target.value || '800', 10))
                  }
                  className="mt-2 bg-background"
                />
              </div>

              <div className="rounded-lg border border-border bg-background/70 p-3 md:col-span-2">
                <label className="block text-xs text-muted-foreground">Embedding model</label>
                <Input
                  type="text"
                  value={ragEmbeddingModel}
                  onChange={(event) => setRagEmbeddingModel(event.target.value)}
                  className="mt-2 bg-background"
                />
              </div>

              <div className="rounded-lg border border-border bg-background/70 p-3 md:col-span-2">
                <label className="block text-xs text-muted-foreground">Generation model</label>
                <Input
                  type="text"
                  value={ragGenerationModel}
                  onChange={(event) => setRagGenerationModel(event.target.value)}
                  className="mt-2 bg-background"
                />
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-border bg-background/70 p-3">
              <label className="block text-sm text-muted-foreground">RAG prompt template</label>
              <TextArea
                className="mt-2 min-h-32 text-sm"
                value={ragPromptTemplate}
                onChange={(event) => setRagPromptTemplate(event.target.value)}
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Used as the system prompt for `/api/rag/suggest`.
              </p>
            </div>

            <div className="mt-4 rounded-lg border border-border bg-background/70 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-foreground">RAG Quality Telemetry</p>
                  <p className="text-xs text-muted-foreground">
                    Last {ragTelemetryLookbackDays} days · updated{' '}
                    {new Date(ragTelemetry.generatedAt).toLocaleString()}
                  </p>
                  <span
                    className={`mt-2 inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${
                      ragTelemetryHealth.severity === 'critical'
                        ? 'border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300'
                        : ragTelemetryHealth.severity === 'watch'
                          ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300'
                          : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                    }`}
                  >
                    {ragTelemetryHealth.label}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={String(ragTelemetryLookbackDays)}
                    onChange={(event) =>
                      setRagTelemetryLookbackDays(
                        normalizeTelemetryLookbackDays(Number.parseInt(event.target.value, 10)),
                      )
                    }
                    className="h-8 rounded-lg border border-border bg-background px-2 text-xs text-foreground focus:border-brand-500 focus:outline-none"
                  >
                    {TELEMETRY_LOOKBACK_OPTIONS.map((days) => (
                      <option key={days} value={days}>
                        {days}d
                      </option>
                    ))}
                  </select>
                  <Button
                    variant="ghost"
                    onClick={refreshRagTelemetry}
                    disabled={loadingRagTelemetry}
                    className="h-8 rounded-lg border border-border bg-transparent px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    {loadingRagTelemetry ? 'Refreshing...' : 'Refresh Telemetry'}
                  </Button>
                </div>
              </div>

              <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-md border border-border bg-card p-2.5">
                  <p className="text-xs text-muted-foreground">Feedback votes</p>
                  <p className="mt-1 text-sm font-semibold text-foreground">
                    {ragTelemetry.feedback.total}
                  </p>
                </div>
                <div className="rounded-md border border-border bg-card p-2.5">
                  <p className="text-xs text-muted-foreground">Acceptance rate</p>
                  <p className="mt-1 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                    {formatPercent(ragTelemetry.feedback.acceptanceRate)}
                  </p>
                </div>
                <div className="rounded-md border border-border bg-card p-2.5">
                  <p className="text-xs text-muted-foreground">Reject rate</p>
                  <p className="mt-1 text-sm font-semibold text-rose-700 dark:text-rose-300">
                    {formatPercent(ragTelemetry.feedback.rejectionRate)}
                  </p>
                </div>
                <div className="rounded-md border border-border bg-card p-2.5">
                  <p className="text-xs text-muted-foreground">Finalized suggestions</p>
                  <p className="mt-1 text-sm font-semibold text-foreground">
                    {ragTelemetry.suggestionStatus.accepted +
                      ragTelemetry.suggestionStatus.applied +
                      ragTelemetry.suggestionStatus.rejected}
                  </p>
                </div>
              </div>

              {ragTelemetryAlerts.length > 0 ? (
                <div className="mt-3 rounded-md border border-border bg-card p-3">
                  <p className="text-xs font-medium text-foreground">Telemetry runbook alerts</p>
                  <div className="mt-2 space-y-2">
                    {ragTelemetryAlerts.map((alert) => (
                      <div
                        key={alert.id}
                        className={`rounded-md border px-2.5 py-2 text-xs ${
                          alert.severity === 'critical'
                            ? 'border-rose-500/30 bg-rose-500/5'
                            : 'border-amber-500/30 bg-amber-500/5'
                        }`}
                      >
                        <p className="font-semibold text-foreground">{alert.title}</p>
                        <p className="mt-1 text-muted-foreground">{alert.detail}</p>
                        <p className="mt-1 text-foreground">Current: {alert.currentValue}</p>
                        <p className="mt-1 text-foreground">
                          Threshold guidance: {alert.thresholdGuidance}
                        </p>
                        <p className="mt-1 text-foreground">Runbook: {alert.runbook}</p>
                        <a
                          href={alert.runbookHref}
                          className="mt-2 inline-flex items-center rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted"
                        >
                          {alert.runbookLabel}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div
                id="rag-telemetry-playbook"
                className="mt-3 rounded-md border border-border bg-card p-3"
              >
                <p className="text-xs font-medium text-foreground">RAG escalation playbook</p>
                <div className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                  <p>
                    1. If sample size is low, increase the lookback window before changing any
                    retrieval thresholds.
                  </p>
                  <p>
                    2. If rejection or low-acceptance alerts persist across two windows, tune Min
                    confidence upward, then reduce Top-K or Max suggestions before widening context.
                  </p>
                  <p>
                    3. If backlog is the only alert, verify Dialer accept/reject workflow capture
                    before changing model or retrieval settings.
                  </p>
                </div>
              </div>

              <div className="mt-3 grid gap-3 xl:grid-cols-2">
                <div className="rounded-md border border-border bg-card p-3">
                  <p className="text-xs font-medium text-foreground">Suggestion status mix</p>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                    <p>
                      Proposed:{' '}
                      <span className="text-foreground">
                        {ragTelemetry.suggestionStatus.proposed}
                      </span>
                    </p>
                    <p>
                      Applied:{' '}
                      <span className="text-foreground">
                        {ragTelemetry.suggestionStatus.applied}
                      </span>
                    </p>
                    <p>
                      Accepted:{' '}
                      <span className="text-foreground">
                        {ragTelemetry.suggestionStatus.accepted}
                      </span>
                    </p>
                    <p>
                      Rejected:{' '}
                      <span className="text-foreground">
                        {ragTelemetry.suggestionStatus.rejected}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="rounded-md border border-border bg-card p-3">
                  <p className="text-xs font-medium text-foreground">Top conversations by votes</p>
                  <div className="mt-2 max-h-28 space-y-1 overflow-y-auto pr-1 text-xs">
                    {ragTelemetry.byConversation.length === 0 ? (
                      <p className="text-muted-foreground">No feedback conversations yet.</p>
                    ) : (
                      ragTelemetry.byConversation.slice(0, 4).map((item) => (
                        <p key={item.conversationId} className="text-muted-foreground">
                          <span className="text-foreground">
                            {item.conversationId.slice(0, 8)}...
                          </span>{' '}
                          · {item.total} votes · {formatPercent(item.acceptanceRate)} accepted
                        </p>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-border bg-card p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-medium text-foreground">
                  Realtime Transcript Telemetry
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Last {realtimeTelemetryLookbackDays} days · updated{' '}
                  {new Date(realtimeTelemetry.generatedAt).toLocaleString()}
                </p>
                <span
                  className={`mt-2 inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${
                    realtimeTelemetryHealth.severity === 'critical'
                      ? 'border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300'
                      : realtimeTelemetryHealth.severity === 'watch'
                        ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300'
                        : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                  }`}
                >
                  {realtimeTelemetryHealth.label}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={String(realtimeTelemetryLookbackDays)}
                  onChange={(event) =>
                    setRealtimeTelemetryLookbackDays(
                      normalizeTelemetryLookbackDays(Number.parseInt(event.target.value, 10)),
                    )
                  }
                  className="h-8 rounded-lg border border-border bg-background px-2 text-xs text-foreground focus:border-brand-500 focus:outline-none"
                >
                  {TELEMETRY_LOOKBACK_OPTIONS.map((days) => (
                    <option key={days} value={days}>
                      {days}d
                    </option>
                  ))}
                </select>
                <Button
                  variant="ghost"
                  onClick={refreshRealtimeTelemetry}
                  disabled={loadingRealtimeTelemetry}
                  className="h-8 rounded-lg border border-border bg-transparent px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  {loadingRealtimeTelemetry ? 'Refreshing...' : 'Refresh Telemetry'}
                </Button>
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-10">
              <div className="rounded-md border border-border bg-card p-2.5">
                <p className="text-xs text-muted-foreground">Transcript chunks</p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  {realtimeTelemetry.totalTranscriptChunks}
                </p>
              </div>
              <div className="rounded-md border border-border bg-card p-2.5">
                <p className="text-xs text-muted-foreground">Fallback rate</p>
                <p className="mt-1 text-sm font-semibold text-amber-700 dark:text-amber-300">
                  {formatPercent(realtimeTelemetry.fallbackRate)}
                </p>
              </div>
              <div className="rounded-md border border-border bg-card p-2.5">
                <p className="text-xs text-muted-foreground">Attempt failure rate</p>
                <p className="mt-1 text-sm font-semibold text-rose-700 dark:text-rose-300">
                  {formatPercent(realtimeTelemetry.attemptFailureRate)}
                </p>
              </div>
              <div className="rounded-md border border-border bg-card p-2.5">
                <p className="text-xs text-muted-foreground">Empty transcript rate</p>
                <p className="mt-1 text-sm font-semibold text-slate-700 dark:text-slate-200">
                  {formatPercent(realtimeTelemetry.emptyTranscriptRate)}
                </p>
              </div>
              <div className="rounded-md border border-border bg-card p-2.5">
                <p className="text-xs text-muted-foreground">Stream stop coverage</p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  {formatPercent(realtimeTelemetry.stopChunkRate)}
                </p>
              </div>
              <div className="rounded-md border border-border bg-card p-2.5">
                <p className="text-xs text-muted-foreground">Unfinalized streams</p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  {realtimeTelemetry.unfinalizedStreams}
                </p>
              </div>
              <div className="rounded-md border border-border bg-card p-2.5">
                <p className="text-xs text-muted-foreground">Unfinalized coverage</p>
                <p className="mt-1 text-sm font-semibold text-amber-700 dark:text-amber-300">
                  {formatPercent(realtimeTelemetry.unfinalizedStreamRate)}
                </p>
              </div>
              <div className="rounded-md border border-border bg-card p-2.5">
                <p className="text-xs text-muted-foreground">Stream failure coverage</p>
                <p className="mt-1 text-sm font-semibold text-rose-700 dark:text-rose-300">
                  {formatPercent(realtimeTelemetry.streamAttemptFailureRate)}
                </p>
              </div>
              <div className="rounded-md border border-border bg-card p-2.5">
                <p className="text-xs text-muted-foreground">Stale unfinalized</p>
                <p className="mt-1 text-sm font-semibold text-rose-700 dark:text-rose-300">
                  {realtimeTelemetry.staleUnfinalizedStreams}
                </p>
              </div>
              <div className="rounded-md border border-border bg-card p-2.5">
                <p className="text-xs text-muted-foreground">Stream empty coverage</p>
                <p className="mt-1 text-sm font-semibold text-slate-700 dark:text-slate-200">
                  {formatPercent(realtimeTelemetry.streamEmptyTranscriptRate)}
                </p>
              </div>
              <div className="rounded-md border border-border bg-card p-2.5">
                <p className="text-xs text-muted-foreground">Avg attempts/chunk</p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  {realtimeTelemetry.averageAttemptsPerChunk.toFixed(2)}
                </p>
              </div>
            </div>

            {realtimeTelemetryAlerts.length > 0 ? (
              <div className="mt-3 rounded-md border border-border bg-card p-3">
                <p className="text-xs font-medium text-foreground">Telemetry runbook alerts</p>
                <div className="mt-2 space-y-2">
                  {realtimeTelemetryAlerts.map((alert) => (
                    <div
                      key={alert.id}
                      className={`rounded-md border px-2.5 py-2 text-xs ${
                        alert.severity === 'critical'
                          ? 'border-rose-500/30 bg-rose-500/5'
                          : 'border-amber-500/30 bg-amber-500/5'
                      }`}
                    >
                      <p className="font-semibold text-foreground">{alert.title}</p>
                      <p className="mt-1 text-muted-foreground">{alert.detail}</p>
                      <p className="mt-1 text-foreground">Current: {alert.currentValue}</p>
                      <p className="mt-1 text-foreground">
                        Threshold guidance: {alert.thresholdGuidance}
                      </p>
                      <p className="mt-1 text-foreground">Runbook: {alert.runbook}</p>
                      <a
                        href={alert.runbookHref}
                        className="mt-2 inline-flex items-center rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted"
                      >
                        {alert.runbookLabel}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="mt-3 grid gap-3 xl:grid-cols-2">
              <div className="rounded-md border border-border bg-card p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-xs font-medium text-foreground">Realtime ingress recovery</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {realtimeTelemetry.ingressDiagnostics.total > 0
                        ? `${realtimeTelemetry.ingressDiagnostics.total} team-scoped ingress validation warnings recorded in this lookback window.`
                        : 'No team-scoped realtime ingress validation warnings recorded in this lookback window.'}
                    </p>
                  </div>
                  <a
                    href="#realtime-ingress-recovery-playbook"
                    className="inline-flex items-center rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted"
                  >
                    Open realtime ingress runbook
                  </a>
                </div>

                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <div className="rounded-md border border-border bg-background/70 p-2.5">
                    <p className="text-xs font-medium text-foreground">Top failure reasons</p>
                    <div className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                      {realtimeIngressRecoveryReasons.length === 0 ? (
                        <p>No team-scoped ingress failures in this window.</p>
                      ) : (
                        realtimeIngressRecoveryReasons.map((reason) => (
                          <div key={reason.reason}>
                            <p className="text-foreground">
                              {reason.label} · {reason.count}
                            </p>
                            <p>
                              <code className="rounded bg-background px-1 py-0.5 text-[11px] text-foreground">
                                {reason.reason}
                              </code>
                              {reason.latestAt
                                ? ` · last seen ${new Date(reason.latestAt).toLocaleString()}`
                                : ''}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background/70 p-2.5">
                    <p className="text-xs font-medium text-foreground">Recent incidents</p>
                    <div className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                      {realtimeTelemetry.ingressDiagnostics.recentIncidents.length === 0 ? (
                        <p>No recent incidents recorded.</p>
                      ) : (
                        realtimeTelemetry.ingressDiagnostics.recentIncidents
                          .slice(0, 3)
                          .map((incident) => (
                            <p
                              key={`${incident.occurredAt}-${incident.reason}-${incident.event}-${incident.callSid ?? 'none'}`}
                            >
                              <span className="text-foreground">{incident.event}</span> ·{' '}
                              <code className="rounded bg-background px-1 py-0.5 text-[11px] text-foreground">
                                {incident.reason}
                              </code>
                              {incident.callSid ? ` · ${incident.callSid}` : ''}
                              {incident.streamSid ? ` · ${incident.streamSid}` : ''}
                            </p>
                          ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-3 rounded-md border border-border bg-background/70 p-2.5">
                  <p className="text-xs font-medium text-foreground">Next actions</p>
                  <div className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                    {realtimeIngressRecoveryActions.map((action, index) => (
                      <p key={action}>
                        {index + 1}. {action}
                      </p>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-border bg-card p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-xs font-medium text-foreground">
                      Twilio non-auth ingress recovery
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Use this when a webhook fails closed before the worker can safely resolve the
                      owning team.
                    </p>
                  </div>
                  <a
                    href="#non-auth-ingress-playbook"
                    className="inline-flex items-center rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted"
                  >
                    Open non-auth ingress runbook
                  </a>
                </div>
                <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                  <p>1. Start with the logged failure code and attempted resolution path.</p>
                  <p>2. Verify the destination number or Twilio account mapping still resolves.</p>
                  <p>3. Retry only after the routing identity is mapped safely back to a team.</p>
                </div>
              </div>
            </div>

            <div
              id="realtime-telemetry-playbook"
              className="mt-3 rounded-md border border-border bg-card p-3"
            >
              <p className="text-xs font-medium text-foreground">Realtime escalation playbook</p>
              <div className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                <p>
                  1. If transcript sample size is low, expand the lookback window before changing
                  alert thresholds or model routing.
                </p>
                <p>
                  2. If fallback, empty-transcript, or attempt-failure alerts persist across two
                  windows, inspect transcript model routing and top attempt errors before reducing
                  alert sensitivity.
                </p>
                <p>
                  3. If stale or missing stop-chunk alerts fire, prioritize websocket close and
                  Twilio stop-event recovery before tuning telemetry thresholds.
                </p>
              </div>
            </div>

            <div className="mt-3 grid gap-3 xl:grid-cols-2">
              <div
                id="realtime-ingress-recovery-playbook"
                className="rounded-md border border-border bg-card p-3"
              >
                <p className="text-xs font-medium text-foreground">
                  Realtime ingress recovery playbook
                </p>
                <div className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                  <p>
                    1. Confirm the signed <code>stream_token</code> and received{' '}
                    <code>streamSid</code> belong to the same live call before replaying the stream.
                  </p>
                  <p>
                    2. If the warning references missing call or conversation data, inspect the
                    persisted call record and team-scoped conversation linkage first.
                  </p>
                  <p>
                    3. Use the non-auth ingress runbook when the failure originated before a team
                    could be resolved safely.
                  </p>
                </div>
              </div>

              <div
                id="non-auth-ingress-playbook"
                className="rounded-md border border-border bg-card p-3"
              >
                <p className="text-xs font-medium text-foreground">
                  Non-auth ingress recovery playbook
                </p>
                <div className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                  <p>
                    1. Check <code>failureCode</code>, <code>resolutionAttempts</code>, and the
                    incoming Twilio identifiers in the worker logs.
                  </p>
                  <p>
                    2. Verify the destination phone number, <code>AccountSid</code>, or recording
                    callback metadata still maps back to an active team configuration.
                  </p>
                  <p>
                    3. Keep broader automation planned until the operator can prove the request
                    should be replayed safely.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-3 grid gap-3 xl:grid-cols-4">
              <div className="rounded-md border border-border bg-card p-3">
                <p className="text-xs font-medium text-foreground">Chunk source mix</p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <p>
                    Chunk:{' '}
                    <span className="text-foreground">{realtimeTelemetry.chunkSources.chunk}</span>
                  </p>
                  <p>
                    Stop:{' '}
                    <span className="text-foreground">{realtimeTelemetry.chunkSources.stop}</span>
                  </p>
                  <p>
                    Other:{' '}
                    <span className="text-foreground">{realtimeTelemetry.chunkSources.other}</span>
                  </p>
                  <p>
                    Calls: <span className="text-foreground">{realtimeTelemetry.uniqueCalls}</span>
                  </p>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Avg transcript chars/chunk: {Math.round(realtimeTelemetry.averageTranscriptChars)}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Empty transcript chunks: {realtimeTelemetry.emptyTranscriptChunks}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Streams missing stop chunk: {realtimeTelemetry.streamsWithoutStopChunk}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Unfinalized stream coverage:{' '}
                  {formatPercent(realtimeTelemetry.unfinalizedStreamRate)}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Stale unfinalized streams: {realtimeTelemetry.staleUnfinalizedStreams}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Streams with retries/failures: {realtimeTelemetry.streamsWithAttemptFailures}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Stream failure coverage:{' '}
                  {formatPercent(realtimeTelemetry.streamAttemptFailureRate)}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Streams with empty transcripts: {realtimeTelemetry.streamsWithEmptyTranscripts}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Stream empty coverage:{' '}
                  {formatPercent(realtimeTelemetry.streamEmptyTranscriptRate)}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Delayed-gap streams: {realtimeTelemetry.streamsWithDelayedGaps}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Delayed-gap coverage: {formatPercent(realtimeTelemetry.streamDelayedGapRate)}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Avg chunk gap: {Math.round(realtimeTelemetry.averageChunkGapSeconds)}s
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Worst observed gap: {Math.round(realtimeTelemetry.largestObservedGapSeconds)}s
                </p>
              </div>

              <div className="rounded-md border border-border bg-card p-3">
                <p className="text-xs font-medium text-foreground">Top transcript models</p>
                <div className="mt-2 max-h-28 space-y-1 overflow-y-auto pr-1 text-xs">
                  {realtimeTelemetry.models.length === 0 ? (
                    <p className="text-muted-foreground">No transcript model data yet.</p>
                  ) : (
                    realtimeTelemetry.models.slice(0, 4).map((model) => (
                      <p key={model.model} className="text-muted-foreground">
                        <span className="text-foreground">{model.model}</span> · {model.chunks}{' '}
                        chunks · {model.fallbackChunks} fallback ·{' '}
                        {formatPercent(model.attemptFailureRate)} attempt-fail ·{' '}
                        {formatPercent(model.emptyTranscriptRate)} empty
                      </p>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-md border border-border bg-card p-3">
                <p className="text-xs font-medium text-foreground">Top attempt errors</p>
                <div className="mt-2 max-h-28 space-y-1 overflow-y-auto pr-1 text-xs">
                  {realtimeTelemetry.topAttemptErrors.length === 0 ? (
                    <p className="text-muted-foreground">No attempt errors recorded.</p>
                  ) : (
                    realtimeTelemetry.topAttemptErrors.slice(0, 4).map((item) => (
                      <p key={item.message} className="text-muted-foreground">
                        <span className="text-foreground">{item.message}</span> · {item.count}
                      </p>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-md border border-border bg-card p-3">
                <p className="text-xs font-medium text-foreground">Trend vs previous window</p>
                <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                  <p>
                    Previous chunks:{' '}
                    <span className="text-foreground">
                      {realtimeTelemetry.trend.previousWindowChunks}
                    </span>
                  </p>
                  <p>
                    Fallback delta:{' '}
                    <span className="text-foreground">
                      {formatSignedPercent(realtimeTelemetry.trend.fallbackRateDelta)}
                    </span>
                  </p>
                  <p>
                    Attempt-failure delta:{' '}
                    <span className="text-foreground">
                      {formatSignedPercent(realtimeTelemetry.trend.attemptFailureRateDelta)}
                    </span>
                  </p>
                  <p>
                    Empty-transcript delta:{' '}
                    <span className="text-foreground">
                      {formatSignedPercent(realtimeTelemetry.trend.emptyTranscriptRateDelta)}
                    </span>
                  </p>
                  <p>
                    Stop-coverage delta:{' '}
                    <span className="text-foreground">
                      {formatSignedPercent(realtimeTelemetry.trend.stopChunkRateDelta)}
                    </span>
                  </p>
                  <p>
                    Stream-failure delta:{' '}
                    <span className="text-foreground">
                      {formatSignedPercent(realtimeTelemetry.trend.streamAttemptFailureRateDelta)}
                    </span>
                  </p>
                  <p>
                    Delayed-gap delta:{' '}
                    <span className="text-foreground">
                      {formatSignedPercent(realtimeTelemetry.trend.streamDelayedGapRateDelta)}
                    </span>
                  </p>
                  <p>
                    Stream-empty delta:{' '}
                    <span className="text-foreground">
                      {formatSignedPercent(realtimeTelemetry.trend.streamEmptyTranscriptRateDelta)}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-3 grid gap-3 xl:grid-cols-3">
              <div className="rounded-md border border-border bg-card p-3">
                <p className="text-xs font-medium text-foreground">Recent streams</p>
                <div className="mt-2 max-h-28 space-y-1 overflow-y-auto pr-1 text-xs">
                  {realtimeTelemetry.recentStreams.length === 0 ? (
                    <p className="text-muted-foreground">No recent stream telemetry yet.</p>
                  ) : (
                    realtimeTelemetry.recentStreams.slice(0, 4).map((stream) => (
                      <p
                        key={`${stream.callSid ?? 'none'}-${stream.streamSid ?? 'none'}-${
                          stream.conversationId
                        }`}
                        className="text-muted-foreground"
                      >
                        <span className="text-foreground">
                          {stream.callSid ? stream.callSid.slice(0, 10) : 'No CallSid'}
                        </span>{' '}
                        · {stream.chunks} chunks · {stream.stopChunks} stop ·{' '}
                        {stream.fallbackChunks} fallback · {stream.emptyTranscriptChunks} empty ·{' '}
                        {stream.delayedGapCount} delayed gaps · max{' '}
                        {Math.round(stream.largestGapSeconds)}s ·{' '}
                        {new Date(stream.latestAt).toLocaleTimeString()}
                      </p>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-md border border-border bg-card p-3">
                <p className="text-xs font-medium text-foreground">Top delayed streams</p>
                <div className="mt-2 max-h-28 space-y-1 overflow-y-auto pr-1 text-xs">
                  {realtimeTelemetry.topDelayedStreams.length === 0 ? (
                    <p className="text-muted-foreground">No delayed chunk gaps in this window.</p>
                  ) : (
                    realtimeTelemetry.topDelayedStreams.slice(0, 4).map((stream) => (
                      <p
                        key={`${stream.callSid ?? 'none'}-${stream.streamSid ?? 'none'}-${
                          stream.conversationId
                        }-delayed`}
                        className="text-muted-foreground"
                      >
                        <span className="text-foreground">
                          {stream.callSid ? stream.callSid.slice(0, 10) : 'No CallSid'}
                        </span>{' '}
                        · {stream.delayedGapCount} delayed gaps · max{' '}
                        {Math.round(stream.largestGapSeconds)}s · {stream.fallbackChunks} fallback
                      </p>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-md border border-border bg-card p-3">
                <p className="text-xs font-medium text-foreground">Top unfinalized streams</p>
                <div className="mt-2 max-h-28 space-y-1 overflow-y-auto pr-1 text-xs">
                  {realtimeTelemetry.topUnfinalizedStreams.length === 0 ? (
                    <p className="text-muted-foreground">No unfinalized streams in this window.</p>
                  ) : (
                    realtimeTelemetry.topUnfinalizedStreams.slice(0, 4).map((stream) => (
                      <p
                        key={`${stream.callSid ?? 'none'}-${stream.streamSid ?? 'none'}-${
                          stream.conversationId
                        }-unfinalized`}
                        className="text-muted-foreground"
                      >
                        <span className="text-foreground">
                          {stream.callSid ? stream.callSid.slice(0, 10) : 'No CallSid'}
                        </span>{' '}
                        · {stream.chunks} chunks · {stream.secondsSinceLastChunk}s since chunk
                      </p>
                    ))
                  )}
                </div>
              </div>
            </div>
          </section>
        </>
      ) : null}

      {activeTopLevelTab === 'integrations' ? (
        <section className="rounded-xl border border-border bg-card p-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-foreground">Telephony Routing Control Plane</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Configure routing policy and provider priorities while keeping live execution
              reversible with one settings change.
            </p>
          </div>

          <RoutingReadinessStrip
            readiness={activeRoutingReadiness}
            title="Routing cutover readiness"
            statuses={[
              {
                label: 'Routing mode',
                value:
                  routingMode === 'provider_router'
                    ? 'New routing (provider router)'
                    : 'Twilio (legacy)',
                tone: routingMode === 'provider_router' ? 'good' : 'watch',
              },
              {
                label: 'Realtime transcript',
                value: realtimeTelemetryHealth.label,
                tone: resolveRealtimeTelemetryTone(realtimeTelemetryHealth.severity),
              },
            ]}
            className="mb-4"
          />

          {requiresProviderRouterCutoverAcknowledgement ? (
            <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
              <p className="text-sm font-semibold text-foreground">
                Confirm provider-router cutover
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                This switches outbound traffic from Twilio legacy mode to provider-router mode.
                Rollback stays one settings change away by switching Routing Mode back to Twilio
                (legacy).
              </p>
              <label className="mt-3 flex items-start gap-2 text-xs text-foreground">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-brand-500"
                  aria-label="I confirm this provider-router cutover"
                  checked={providerRouterCutoverAcknowledged}
                  onChange={(event) => setProviderRouterCutoverAcknowledged(event.target.checked)}
                />
                <span>
                  I confirm this provider-router cutover and understand rollback remains available
                  through Routing Mode.
                </span>
              </label>
            </div>
          ) : null}

          <div className="grid gap-4 rounded-xl border border-border bg-background/50 p-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="block text-sm text-muted-foreground">Routing Mode</label>
              <select
                aria-label="Routing Mode"
                value={routingMode}
                onChange={(event) => {
                  const nextRoutingMode =
                    event.target.value === 'provider_router' ? 'provider_router' : 'twilio_legacy';
                  setRoutingMode(nextRoutingMode);
                  if (nextRoutingMode !== 'provider_router') {
                    setProviderRouterCutoverAcknowledged(false);
                    return;
                  }
                  setProviderRouterCutoverAcknowledged(savedRoutingMode === 'provider_router');
                }}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
              >
                <option value="twilio_legacy">Twilio (legacy)</option>
                <option value="provider_router">New routing (provider router)</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-muted-foreground">
                Default Number Owner Provider
              </label>
              <select
                aria-label="Default Number Owner Provider"
                value={defaultNumberOwnerProvider}
                onChange={(event) =>
                  setDefaultNumberOwnerProvider(
                    normalizeRoutingProvider(event.target.value, defaultNumberOwnerProvider),
                  )
                }
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
              >
                {ROUTING_PROVIDER_OPTIONS.map((provider) => (
                  <option key={`owner-${provider}`} value={provider}>
                    {provider}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-muted-foreground">Voice Primary Provider</label>
              <select
                aria-label="Voice Primary Provider"
                value={voicePrimaryProvider}
                onChange={(event) =>
                  setVoicePrimaryProvider(
                    normalizeRoutingProvider(event.target.value, voicePrimaryProvider),
                  )
                }
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
              >
                {ROUTING_PROVIDER_OPTIONS.map((provider) => (
                  <option key={`voice-primary-${provider}`} value={provider}>
                    {provider}
                  </option>
                ))}
              </select>
              <div className="rounded-md border border-border bg-card p-2">
                <p className="text-xs font-medium text-foreground">Voice failover order</p>
                <div className="mt-2 space-y-1">
                  {voiceFailoverCandidates.map((provider) => (
                    <label
                      key={`voice-failover-${provider}`}
                      className="flex items-center gap-2 text-xs text-muted-foreground"
                    >
                      <input
                        type="checkbox"
                        aria-label={`Voice failover provider ${provider}`}
                        checked={voiceFailoverProviders.includes(provider)}
                        onChange={(event) => {
                          setVoiceFailoverProviders((current) => {
                            if (event.target.checked) {
                              return sanitizeFailoverProviders(
                                [...current, provider],
                                voicePrimaryProvider,
                              );
                            }
                            return sanitizeFailoverProviders(
                              current.filter((entry) => entry !== provider),
                              voicePrimaryProvider,
                            );
                          });
                        }}
                      />
                      <span>{provider}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-muted-foreground">SMS Primary Provider</label>
              <select
                aria-label="SMS Primary Provider"
                value={smsPrimaryProvider}
                onChange={(event) =>
                  setSmsPrimaryProvider(
                    normalizeRoutingProvider(event.target.value, smsPrimaryProvider),
                  )
                }
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
              >
                {ROUTING_PROVIDER_OPTIONS.map((provider) => (
                  <option key={`sms-primary-${provider}`} value={provider}>
                    {provider}
                  </option>
                ))}
              </select>
              <div className="rounded-md border border-border bg-card p-2">
                <p className="text-xs font-medium text-foreground">SMS failover order</p>
                <div className="mt-2 space-y-1">
                  {smsFailoverCandidates.map((provider) => (
                    <label
                      key={`sms-failover-${provider}`}
                      className="flex items-center gap-2 text-xs text-muted-foreground"
                    >
                      <input
                        type="checkbox"
                        aria-label={`SMS failover provider ${provider}`}
                        checked={smsFailoverProviders.includes(provider)}
                        onChange={(event) => {
                          setSmsFailoverProviders((current) => {
                            if (event.target.checked) {
                              return sanitizeFailoverProviders(
                                [...current, provider],
                                smsPrimaryProvider,
                              );
                            }
                            return sanitizeFailoverProviders(
                              current.filter((entry) => entry !== provider),
                              smsPrimaryProvider,
                            );
                          });
                        }}
                      />
                      <span>{provider}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-border bg-background/50 p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div>
                <h4 className="text-md font-medium text-foreground">Number Inventory</h4>
                <p className="mt-1 text-xs text-muted-foreground">
                  Track number ownership and provider assignment for routing cutover decisions.
                </p>
              </div>
              <Button
                type="button"
                onClick={addNumberInventoryRow}
                className="h-8 bg-secondary px-3 text-xs text-secondary-foreground hover:bg-secondary/80"
              >
                Add number
              </Button>
            </div>

            {numberInventory.length === 0 ? (
              <p className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
                No numbers configured yet.
              </p>
            ) : (
              <div className="space-y-2">
                {numberInventory.map((row, index) => (
                  <div
                    key={`inventory-row-${index}`}
                    className="grid gap-2 rounded-md border border-border bg-card p-2 md:grid-cols-5"
                  >
                    <Input
                      aria-label={`Inventory Number ${index + 1}`}
                      placeholder="+14155550123"
                      value={row.phoneNumber}
                      onChange={(event) =>
                        updateNumberInventoryRow(index, { phoneNumber: event.target.value })
                      }
                      className="bg-background"
                    />
                    <select
                      aria-label={`Owner Provider ${index + 1}`}
                      value={row.ownerProvider}
                      onChange={(event) =>
                        updateNumberInventoryRow(index, {
                          ownerProvider: normalizeRoutingProvider(
                            event.target.value,
                            row.ownerProvider,
                          ),
                        })
                      }
                      className="rounded-md border border-border bg-background px-3 py-2 text-xs text-foreground"
                    >
                      {ROUTING_PROVIDER_OPTIONS.map((provider) => (
                        <option key={`inventory-owner-${index}-${provider}`} value={provider}>
                          owner: {provider}
                        </option>
                      ))}
                    </select>
                    <select
                      aria-label={`SMS Provider ${index + 1}`}
                      value={row.smsProvider}
                      onChange={(event) =>
                        updateNumberInventoryRow(index, {
                          smsProvider: normalizeRoutingProvider(
                            event.target.value,
                            row.smsProvider,
                          ),
                        })
                      }
                      className="rounded-md border border-border bg-background px-3 py-2 text-xs text-foreground"
                    >
                      {ROUTING_PROVIDER_OPTIONS.map((provider) => (
                        <option key={`inventory-sms-${index}-${provider}`} value={provider}>
                          sms: {provider}
                        </option>
                      ))}
                    </select>
                    <select
                      aria-label={`Voice Provider ${index + 1}`}
                      value={row.voiceProvider}
                      onChange={(event) =>
                        updateNumberInventoryRow(index, {
                          voiceProvider: normalizeRoutingProvider(
                            event.target.value,
                            row.voiceProvider,
                          ),
                        })
                      }
                      className="rounded-md border border-border bg-background px-3 py-2 text-xs text-foreground"
                    >
                      {ROUTING_PROVIDER_OPTIONS.map((provider) => (
                        <option key={`inventory-voice-${index}-${provider}`} value={provider}>
                          voice: {provider}
                        </option>
                      ))}
                    </select>
                    <div className="flex gap-2">
                      <select
                        aria-label={`Migration State ${index + 1}`}
                        value={row.migrationState}
                        onChange={(event) =>
                          updateNumberInventoryRow(index, {
                            migrationState: normalizeNumberMigrationState(
                              event.target.value,
                              row.migrationState,
                            ),
                          })
                        }
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-xs text-foreground"
                      >
                        {TELEPHONY_NUMBER_MIGRATION_OPTIONS.map((state) => (
                          <option key={`inventory-state-${index}-${state}`} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        onClick={() => removeNumberInventoryRow(index)}
                        className="h-9 bg-destructive px-3 text-xs text-destructive-foreground hover:bg-destructive/85"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {routingWarnings.length > 0 ? (
            <div className="mt-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
                Routing warnings
              </p>
              <ul className="mt-1 list-disc pl-4 text-xs text-amber-700 dark:text-amber-300">
                {routingWarnings.map((warning, index) => (
                  <li key={`routing-warning-${index}`}>{warning}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="mb-4 mt-6">
            <h3 className="text-lg font-medium text-foreground">Twilio Integration</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Configure tenant credentials. Saved values override default Cloudflare environment
              variables.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="block text-sm text-muted-foreground">Account SID</label>
              <Input
                type="text"
                placeholder="AC..."
                value={twilioSid}
                onChange={(event) => setTwilioSid(event.target.value)}
                className="bg-background"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-muted-foreground">Auth Token</label>
              <Input
                type="password"
                placeholder="Required for initial setup and webhooks"
                value={twilioToken}
                onChange={(event) => setTwilioToken(event.target.value)}
                className="bg-background"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-muted-foreground">
                API Key (Optional, Recommended)
              </label>
              <Input
                type="text"
                placeholder="SK..."
                value={twilioApiKey}
                onChange={(event) => setTwilioApiKey(event.target.value)}
                className="bg-background"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-muted-foreground">API Secret (Optional)</label>
              <Input
                type="password"
                placeholder="Required if using API key"
                value={twilioApiSecret}
                onChange={(event) => setTwilioApiSecret(event.target.value)}
                className="bg-background"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label className="block text-sm text-muted-foreground">Twilio Phone Number</label>
              <Input
                type="text"
                placeholder="+1234567890"
                value={twilioPhoneNumber}
                onChange={(event) => setTwilioPhoneNumber(event.target.value)}
                className="bg-background"
              />
            </div>
          </div>

          <div className="mb-4 mt-6">
            <h3 className="text-lg font-medium text-foreground">Telnyx Integration</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Configure Telnyx credentials for provider-router outbound SMS.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="block text-sm text-muted-foreground">API Key</label>
              <Input
                type="password"
                placeholder="KEY..."
                value={telnyxApiKey}
                onChange={(event) => setTelnyxApiKey(event.target.value)}
                className="bg-background"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm text-muted-foreground">
                Messaging Profile ID (Optional)
              </label>
              <Input
                type="text"
                placeholder="4001..."
                value={telnyxMessagingProfileId}
                onChange={(event) => setTelnyxMessagingProfileId(event.target.value)}
                className="bg-background"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <label className="block text-sm text-muted-foreground">Sender Phone Number</label>
              <Input
                type="text"
                placeholder="+1234567890"
                value={telnyxPhoneNumber}
                onChange={(event) => setTelnyxPhoneNumber(event.target.value)}
                className="bg-background"
              />
            </div>
          </div>

          <div className="mb-4 mt-6">
            <h3 className="text-lg font-medium text-foreground">SignalWire Integration</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Configure SignalWire credentials for staged provider-router failover.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="block text-sm text-muted-foreground">Space URL</label>
              <Input
                type="text"
                placeholder="https://your-space.signalwire.com"
                value={signalwireSpaceUrl}
                onChange={(event) => setSignalwireSpaceUrl(event.target.value)}
                className="bg-background"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm text-muted-foreground">Project ID</label>
              <Input
                type="text"
                placeholder="project-id"
                value={signalwireProjectId}
                onChange={(event) => setSignalwireProjectId(event.target.value)}
                className="bg-background"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm text-muted-foreground">API Token</label>
              <Input
                type="password"
                placeholder="token"
                value={signalwireApiToken}
                onChange={(event) => setSignalwireApiToken(event.target.value)}
                className="bg-background"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm text-muted-foreground">Sender Phone Number</label>
              <Input
                type="text"
                placeholder="+1234567890"
                value={signalwirePhoneNumber}
                onChange={(event) => setSignalwirePhoneNumber(event.target.value)}
                className="bg-background"
              />
            </div>
          </div>

          <div className="mt-6 border-t border-border pt-6">
            <h4 className="mb-2 text-md font-medium text-foreground">
              Sync to Cloudflare Secrets Store
            </h4>
            <p className="mb-4 text-sm text-muted-foreground">
              Push credentials directly to the API Worker secrets store. This updates runtime
              secrets and triggers a worker restart.
            </p>
            <Button
              onClick={handlePushSecrets}
              disabled={pushingSecrets || loading || saving}
              className="bg-indigo-600 text-white transition-colors hover:bg-indigo-500"
            >
              {pushingSecrets ? 'Pushing to Cloudflare...' : 'Push to Cloudflare Secrets Store'}
            </Button>
          </div>

          <div className="mt-6 border-t border-border pt-6">
            <h4 className="mb-2 text-md font-medium text-foreground">Carrier webhook readiness</h4>
            <p className="mb-3 text-sm text-muted-foreground">
              Twilio remains live. Telnyx and SignalWire webhook endpoints are reserved for setup
              now, while provider-normalized routing remains planned under P7.
            </p>

            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="min-w-full divide-y divide-border text-left text-xs">
                <thead className="bg-background/80 text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Provider</th>
                    <th className="px-3 py-2 font-medium">Channel</th>
                    <th className="px-3 py-2 font-medium">Readiness</th>
                    <th className="px-3 py-2 font-medium">Webhook URL</th>
                    <th className="px-3 py-2 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-card">
                  {providerWebhookReadiness.map((row) => (
                    <tr key={`${row.provider}-${row.channel}`}>
                      <td className="px-3 py-2 text-foreground">{row.provider}</td>
                      <td className="px-3 py-2 text-foreground">{row.channel}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded-full px-2 py-0.5 ${
                            row.readiness === 'Live'
                              ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                              : 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
                          }`}
                        >
                          {row.readiness}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                        {row.absoluteUrl}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Not yet resolved/planned: carrier signature validation, payload normalization, and
              full multi-provider routing behavior.
            </p>
          </div>
        </section>
      ) : null}

      {activeTopLevelTab === 'ai' && aiSettingsView === 'training' ? (
        <>
          <section className="rounded-xl border border-border bg-card p-6">
            <h3 className="text-lg font-medium text-foreground">Fine-tuning Controls</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              AI outputs update on call end or manual refresh. Feedback drives model-parameter
              recommendations.
            </p>

            <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <label className="flex items-center gap-2 rounded-lg border border-border bg-background/70 p-3 text-sm text-foreground">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-brand-500"
                  checked={trainingEnabled}
                  onChange={(event) => setTrainingEnabled(event.target.checked)}
                />
                Enable training loop
              </label>
              <label className="flex items-center gap-2 rounded-lg border border-border bg-background/70 p-3 text-sm text-foreground">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-brand-500"
                  checked={trainingAutoApplyOnCallEnd}
                  onChange={(event) => setTrainingAutoApplyOnCallEnd(event.target.checked)}
                />
                Auto-apply on call end
              </label>
              <div className="rounded-lg border border-border bg-background/70 p-3">
                <label className="block text-xs text-muted-foreground">Lookback (days)</label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={String(trainingLookbackDays)}
                  onChange={(event) =>
                    setTrainingLookbackDays(Number.parseInt(event.target.value || '1', 10))
                  }
                  className="mt-2 bg-background"
                />
              </div>
              <div className="rounded-lg border border-border bg-background/70 p-3">
                <label className="block text-xs text-muted-foreground">Minimum samples</label>
                <Input
                  type="number"
                  min={1}
                  max={200}
                  value={String(trainingMinSamples)}
                  onChange={(event) =>
                    setTrainingMinSamples(Number.parseInt(event.target.value || '1', 10))
                  }
                  className="mt-2 bg-background"
                />
              </div>
              <div className="rounded-lg border border-border bg-background/70 p-3">
                <label className="block text-xs text-muted-foreground">Negative threshold</label>
                <Input
                  type="number"
                  min={0}
                  max={1}
                  step={0.01}
                  value={String(trainingNegativeThreshold)}
                  onChange={(event) =>
                    setTrainingNegativeThreshold(Number.parseFloat(event.target.value || '0'))
                  }
                  className="mt-2 bg-background"
                />
              </div>
              <div className="rounded-lg border border-border bg-background/70 p-3">
                <label className="block text-xs text-muted-foreground">Positive threshold</label>
                <Input
                  type="number"
                  min={0}
                  max={1}
                  step={0.01}
                  value={String(trainingPositiveThreshold)}
                  onChange={(event) =>
                    setTrainingPositiveThreshold(Number.parseFloat(event.target.value || '0'))
                  }
                  className="mt-2 bg-background"
                />
              </div>
              <div className="rounded-lg border border-border bg-background/70 p-3">
                <label className="block text-xs text-muted-foreground">Temperature step</label>
                <Input
                  type="number"
                  min={0.01}
                  max={0.5}
                  step={0.01}
                  value={String(trainingTemperatureStep)}
                  onChange={(event) =>
                    setTrainingTemperatureStep(Number.parseFloat(event.target.value || '0.01'))
                  }
                  className="mt-2 bg-background"
                />
              </div>
              <div className="rounded-lg border border-border bg-background/70 p-3">
                <label className="block text-xs text-muted-foreground">Max tokens step</label>
                <Input
                  type="number"
                  min={1}
                  max={600}
                  step={1}
                  value={String(trainingMaxTokensStep)}
                  onChange={(event) =>
                    setTrainingMaxTokensStep(Number.parseInt(event.target.value || '1', 10))
                  }
                  className="mt-2 bg-background"
                />
              </div>
            </div>
          </section>

          <div className="grid gap-6 xl:grid-cols-2">
            <section className="rounded-xl border border-border bg-card p-6">
              <h3 className="text-lg font-medium text-foreground">Feedback Stats</h3>
              <div className="mt-3 grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-border bg-background/70 p-3">
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="mt-1 text-xl font-semibold text-foreground">
                    {trainingStats.total}
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-background/70 p-3">
                  <p className="text-xs text-muted-foreground">Positive</p>
                  <p className="mt-1 text-xl font-semibold text-emerald-600 dark:text-emerald-400">
                    {formatPercent(trainingStats.positiveRate)}
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-background/70 p-3">
                  <p className="text-xs text-muted-foreground">Negative</p>
                  <p className="mt-1 text-xl font-semibold text-rose-600 dark:text-rose-400">
                    {formatPercent(trainingStats.negativeRate)}
                  </p>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                {(
                  Object.keys(trainingStats.byTarget) as Array<keyof AiFeedbackStats['byTarget']>
                ).map((targetKey) => {
                  const bucket =
                    trainingStats.byTarget[targetKey] ?? EMPTY_FEEDBACK_STATS.byTarget[targetKey];
                  return (
                    <div
                      key={targetKey}
                      className="rounded-lg border border-border bg-background/70 p-3 text-sm"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-foreground">
                          {formatFeedbackTarget(targetKey as AiFeedbackEntry['targetType'])}
                        </span>
                        <span className="text-xs text-muted-foreground">{bucket.total} votes</span>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        👍 {formatPercent(bucket.positiveRate)} • 👎{' '}
                        {formatPercent(bucket.negativeRate)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="rounded-xl border border-border bg-card p-6">
              <h3 className="text-lg font-medium text-foreground">Recommendation</h3>
              <div className="mt-3 rounded-xl border border-brand-500/30 bg-brand-500/10 p-4">
                <p className="text-sm text-foreground">
                  Current params: temp {normalizedTemperature.toFixed(2)} • max tokens {maxTokens}
                </p>
                <p className="mt-1 text-sm text-foreground">
                  Suggested params: temp{' '}
                  {trainingRecommendation.nextModelParams.temperature.toFixed(2)} • max tokens{' '}
                  {trainingRecommendation.nextModelParams.maxTokens}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  {trainingRecommendation.hasChanges
                    ? 'Recommendation is ready to apply.'
                    : 'No parameter changes recommended right now.'}
                </p>
              </div>
              <ul className="mt-3 space-y-2">
                {trainingRecommendation.reasons.length === 0 ? (
                  <li className="text-xs text-muted-foreground">No recommendations yet.</li>
                ) : (
                  trainingRecommendation.reasons.map((reason, index) => (
                    <li key={`${reason}-${index}`} className="text-xs text-muted-foreground">
                      • {reason}
                    </li>
                  ))
                )}
              </ul>
            </section>
          </div>

          <section className="rounded-xl border border-border bg-card p-6">
            <h3 className="text-lg font-medium text-foreground">Recent Feedback</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Latest thumbs signals captured from conversation insight blocks.
            </p>
            <div className="mt-4 max-h-80 space-y-2 overflow-y-auto pr-1">
              {recentFeedback.length === 0 ? (
                <p className="rounded-lg border border-border bg-background/70 p-3 text-sm text-muted-foreground">
                  No feedback yet.
                </p>
              ) : (
                recentFeedback.map((entry) => (
                  <div
                    key={entry.id}
                    className="rounded-lg border border-border bg-background/70 p-3 text-sm"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-medium text-foreground">
                        {formatFeedbackTarget(entry.targetType)}
                      </p>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          entry.signal === 'up'
                            ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
                            : 'bg-rose-500/20 text-rose-700 dark:text-rose-300'
                        }`}
                      >
                        {entry.signal === 'up' ? 'Helpful' : 'Not helpful'}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {new Date(entry.createdAt).toLocaleString()}
                    </p>
                    {entry.note ? (
                      <p className="mt-2 text-xs text-foreground">{entry.note}</p>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
